'use strict';

/**
 * SampleWorkspace-Provider
 *
 * The bespoke per-sample workspace rebuilt from the LIMS `web/lims` Sample read/edit/create/view screens.
 * Samples are customer-scoped (MyCustomer auth; IDProject is a filter, not a parent), so this lives at the
 * top level (Materials Program) reached by a literal GUID bridge `#/PSRS/Sample/View/:GUIDRecord` that shadows
 * the generic PSRS read view — the PhysicalAsset -> AssetWorkspace precedent.
 *
 * The create/edit/view FORMS are "configuration-based": rendered from the customer's configured ManifestSchema
 * (Type=RecordManifest, SubType=LIMS-Sample-{Create,Edit,View}) loaded via the shared manifest-packaging-service
 * (ManifestPackagingService.loadRecordManifestSchema), falling back to a bundled default. The form is hosted in the
 * SampleFormHost (pict-section-form-host, DataAddress 'AppData' so descriptors SampleDetails.* marshal to
 * AppData.SampleDetails.* exactly like LIMS). Persistence goes through the ported LIMS SampleProvider
 * (createSample: QR minting, storage-location join, "Sample created" log; updateSample: PUT Sample/Upsert).
 *
 * See memory config-app-lims-samples.
 */

const libWorkspace = require('pict-section-workspace');
const DEFAULT_SAMPLE_VIEW_MANIFEST = require('./Default-Sample-View-Manifest.json');
const DEFAULT_SAMPLE_EDIT_MANIFEST = require('./Default-Sample-Edit-Manifest.json');
const DEFAULT_SAMPLE_CREATE_MANIFEST = require('./Default-Sample-Create-Manifest.json');
// The entity popover layouts legacy LIMS shipped (web/lims/source/views/defaultSummaryManifests.json).
const DEFAULT_SUMMARY_MANIFESTS = require('./Default-Summary-Manifests.json');
const libSampleWorkspaceLocationTab = require('./SampleWorkspace-LocationTab.js');
const libSampleDocumentDetails = require('./SampleDocument-Details.js');
const libSampleTestRules = require('@paviasystems/headlight-samples').SampleTestRules;

const TABS = [ 'details', 'location', 'lineitems', 'testinstances', 'documents', 'cores', 'logs', 'print' ];

// LIMS product string — the manifest SubType is <Product>-Sample-<Verb> (e.g. LIMS-Sample-View). MUST be 'LIMS' so
// the config app renders the SAME per-customer form the LIMS app does.
const LIMS_PRODUCT = 'LIMS';

// SampleWorkspace — refactored onto pict-section-workspace (the base now supports view/edit/CREATE modes + an
// EditContent form body). The base owns the shell (mode-aware header, tab bar in view mode, the form body in
// edit/create); this subclass keeps all of Sample's data + the config-driven manifest form + mutations, and the
// per-tab tables (ported from the old view). Config in [[Sample.config.js]].
class SampleWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		this._idSample = 0;
		this._guidSample = '';
		this._activeTab = 'details';
		/** @type {Record<String, import('@paviasystems/headlight-samples/source/SampleTest-Rules.js').FormTestCatalog>} - IDCustomer -> the tests each form type declares. */
		this._formTestCatalogs = {};
		/** @type {{ Key: String, Counts: Record<number, Record<String, number>> }} - media counts for the documents named in Key. */
		this._documentMedia = { Key: '', Counts: {} };
		/** @type {{ Key: String, Media: Array<Object> }} - photos and videos of the unreadable documents named in Key. */
		this._hiddenDocumentMedia = { Key: '', Media: [] };
		this._mode = 'view'; // 'view' | 'edit' | 'create'
		this._cloneFromGUID = '';
		this._activatedTabs = {};
		this._tabLoads = {};
		this._manifestsByVerb = {};
		/** @type {{ Create: boolean, Edit: boolean }|null} */
		this._formsOff = null;
		/** @type {Map<Record<string, any>, Promise<void>>} */
		this._initialBundleGathers = new Map();
		this._statusVocabularyLoaded = false;
		this._referenceBundleLoaded = false;
		this._summaryManifestsLoad = null;
		/** @type {Record<number, string>} IDUser -> display name, for "by <user>" lines. */
		this._userNames = {};
		this._pendingDocumentOpen = 0;
		// The Location tab: where the sample is, and moving it with the Scan Samples cart.
		this._locationTab = new libSampleWorkspaceLocationTab(this);
	}

	get app() { return this.pict.PictApplication; }
	get restClient() { return this.pict.fable.HeadlightRestClient; }
	get permissions() { return this.pict.fable.EffectivePermissionsService; }
	get view() { return this.pict.views.SampleWorkspace; }
	get formHost() { return this.pict.providers.SampleFormHost; }
	get idSample() { return this._idSample; }
	get mode() { return this._mode; }

	get state()
	{
		if (!this.pict.AppData.SampleWorkspace)
		{
			this.pict.AppData.SampleWorkspace =
			{
				Sample: {}, Tab: 'details', Mode: 'view', Loading: false, Message: '', Saving: false,
				LineItems: [], TestInstanceBundle: { TestInstances: [], TestMap: {}, TestInstanceDocumentMap: {} },
				Documents: [], InaccessibleDocumentCount: 0, InaccessibleDocumentIDs: [], HiddenDocumentObservationJoins: null, Cores: [], Logs: [], StatusVocabulary: {}, NewLogNotes: '',
			};
		}
		return this.pict.AppData.SampleWorkspace;
	}

	// --- permissions (fail-open until the capability map is ready) -------------------------------------------
	_canHash(pHash)
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || !tmpPermissions.currentUserReady || !tmpPermissions.currentUserReady())
		{
			return true;
		}
		// A role in the sample's project (a project engineer's, say) can grant what the customer-level map doesn't.
		const tmpIDProject = Number((this.state.Sample || {}).IDProject) || 0;
		if (tmpIDProject && (typeof tmpPermissions.canHashInProject === 'function'))
		{
			return !!tmpPermissions.canHashInProject(pHash, tmpIDProject);
		}
		return !!tmpPermissions.canHash(pHash);
	}
	/** While a sample loads, its project's grants aren't known yet, so loading shows instead of the lock. */
	canRead() { return !!this.state.Loading || this._canHash('Sample-Read'); }
	canCreate() { return this._canHash('Sample-Create') && !this.isFormOff('Create'); }
	canUpdate() { return this._canHash('Sample-Update'); }
	/** Edit the sample's own fields. Documents and notes need only canUpdate; line items and tests need canChangeSample. */
	canEditForm() { return this.canUpdate() && !this.isFormOff('Edit') && !this.isLockedForUser(); }
	/**
	 * Add, change or remove the sample's line items and tests.
	 * @return {boolean}
	 */
	canChangeSample()
	{
		return this.canUpdate() && !this.isLockedForUser();
	}

	// --- sample lock (browser-side only: the API doesn't refuse changes to a locked sample) ------------------
	/**
	 * @return {boolean} whether the loaded sample is locked
	 */
	isLocked()
	{
		const tmpSample = this.state.Sample || {};
		return !!(tmpSample.Locked && (Number(tmpSample.Locked) !== 0));
	}
	/**
	 * Fails closed, unlike _canHash: an override is never granted before the capability map is ready.
	 * @return {boolean}
	 */
	canOverrideLock()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || !tmpPermissions.currentUserReady || !tmpPermissions.currentUserReady())
		{
			return false;
		}
		return !!tmpPermissions.canHash('SampleLock-Override');
	}
	/**
	 * @return {boolean} whether the lock applies to this user
	 */
	isLockedForUser()
	{
		return this.isLocked() && !this.canOverrideLock();
	}
	/**
	 * Tell the user the sample is locked.
	 */
	_toastLocked()
	{
		this._toast('This sample is locked, so it can\'t be changed.', 'error');
	}

	// --- per-customer form availability ------------------------------------------------------------------------
	// A customer whose LIMS-Sample-Create or -Edit manifest exists with no fields doesn't create or edit samples
	// through the form (LADOTD and RIDOT on 2026-10-28). No row at all means the bundled default form.
	// Same rule as legacy LIMS applySampleDisabledState (web/lims/source/LIMS-Application-Web.js).
	async loadFormAvailabilityAsync()
	{
		const [ tmpCreate, tmpEdit ] = await Promise.all([ this._loadManifestAsync('Create'), this._loadManifestAsync('Edit') ]);
		const fEmpty = (pManifest) => !!(pManifest && pManifest.Descriptors && (Object.keys(pManifest.Descriptors).length === 0));
		this._formsOff = { Create: fEmpty(tmpCreate), Edit: fEmpty(tmpEdit) };
		return this._formsOff;
	}
	isFormOff(pVerb) { return !!(this._formsOff && this._formsOff[pVerb]); }

	/** Create mode, unless this customer has no create form: then back to the list. */
	async startCreateAsync(pArg)
	{
		await this.loadFormAvailabilityAsync();
		if (this.isFormOff('Create'))
		{
			this._toast('New samples aren\'t entered here for your organization.', 'info');
			this.backToList();
			return;
		}
		return super.startCreateAsync(pArg);
	}

	/** View or edit; edit falls back to view when this customer has no edit form. */
	async loadAsync(pID, pTab, pMode)
	{
		let tmpMode = pMode;
		if (tmpMode === 'edit')
		{
			await this.loadFormAvailabilityAsync();
			if (this.isFormOff('Edit'))
			{
				tmpMode = 'view';
				this._toast('Sample details aren\'t edited here for your organization.', 'info');
				if ((typeof window !== 'undefined') && window.history && (typeof window.history.replaceState === 'function'))
				{
					window.history.replaceState(null, '', `#/Sample/Workspace/${parseInt(pID, 10) || 0}`);
				}
			}
		}
		return super.loadAsync(pID, pTab, tmpMode);
	}
	canDelete() { return this._canHash('Sample-Delete'); }

	// --- REST helpers ----------------------------------------------------------------------------------------
	_getJSON(pURL)
	{
		return new Promise((resolve) =>
		{
			this.restClient.getJSON(pURL, (pError, pResponse, pBody) =>
			{
				if (pError) { resolve(null); return; }
				resolve((pBody !== undefined) ? pBody : pResponse);
			});
		});
	}

	_toast(pMessage, pType)
	{
		try
		{
			const tmpPanels = this.pict.views.HeadlightPanels;
			if (tmpPanels && (typeof tmpPanels.toast === 'function'))
			{
				tmpPanels.toast({ type: (pType === 'error') ? 'is-danger' : (pType === 'success' ? 'is-success' : 'is-info'), message: pMessage });
				return;
			}
		}
		catch (pError) { /* best-effort */ }
		this.pict.log.info(`SampleWorkspace: ${pMessage}`);
	}

	// --- status vocabulary (code -> label), mirrors LIMS loadSampleStatusVocabulary/resolveSampleStatusName -----
	async ensureStatusVocabularyAsync()
	{
		if (this._statusVocabularyLoaded) { return; }
		this._statusVocabularyLoaded = true;
		const tmpList = await this._getJSON('SampleStatuss/FilteredTo/FSF~Ordinal~ASC~0/0/500');
		const tmpMap = {};
		if (Array.isArray(tmpList))
		{
			for (const tmpRow of tmpList)
			{
				if (tmpRow && tmpRow.Hash) { tmpMap[String(tmpRow.Hash)] = tmpRow.Name || tmpRow.Hash; }
			}
		}
		this.state.StatusVocabulary = tmpMap;
	}

	resolveSampleStatusName(pCode)
	{
		if (!pCode) { return ''; }
		const tmpMap = this.state.StatusVocabulary || {};
		return tmpMap[String(pCode)] || String(pCode);
	}

	// --- entry points (base owns openByGUIDAsync/loadAsync/startCreateAsync; these hooks seed state) -----------

	/** CREATE-mode seed (called by the base startCreateAsync). Clone copies descriptor values (not identity fields). */
	/**
	 * Stash a one-shot prefill applied by the next create — a dashboard's "take a sample" for a sampling-plan line.
	 * @param {{ IDProject?: number, IDLineItem?: number, IDMaterial?: number, SampleType?: string }|null} pPrefill
	 */
	prefillNew(pPrefill)
	{
		this._pendingPrefill = (pPrefill && typeof pPrefill === 'object') ? pPrefill : null;
	}

	async hydrateCreateAsync(pCloneFromGUID)
	{
		this._initialBundleGathers = new Map();
		this._idSample = 0;
		this._guidSample = '';
		this._cloneFromGUID = pCloneFromGUID || '';
		this._activeTab = 'details';
		this._resetTabLoads();
		const tmpState = this.state;
		tmpState.Mode = 'create';
		tmpState.Tab = 'details';
		tmpState.Message = '';
		tmpState.Sample = {};
		tmpState.LineItems = []; tmpState.Documents = []; tmpState.InaccessibleDocumentCount = 0; tmpState.InaccessibleDocumentIDs = []; tmpState.HiddenDocumentObservationJoins = null; tmpState.Cores = []; tmpState.Logs = [];
		tmpState.TestInstanceBundle = { TestInstances: [], TestMap: {}, TestInstanceDocumentMap: {} };
		await this.ensureStatusVocabularyAsync();
		await this._loadReferenceBundleAsync();
		let tmpSeed = { Extension: {} };
		if (this._cloneFromGUID)
		{
			const tmpList = await this._getJSON(`Samples/FilteredTo/FBV~GUIDSample~EQ~${encodeURIComponent(this._cloneFromGUID)}`);
			const tmpSource = (Array.isArray(tmpList) && tmpList.length) ? tmpList[0] : null;
			if (tmpSource)
			{
				tmpSeed = Object.assign({}, tmpSource);
				delete tmpSeed.IDSample; delete tmpSeed.GUIDSample; delete tmpSeed.QRCode;
				delete tmpSeed.LabControlNumber; delete tmpSeed.LabReferenceNumber;
				delete tmpSeed.CreateDate; delete tmpSeed.UpdateDate; delete tmpSeed.Status;
			}
		}
		if (this._pendingPrefill)
		{
			await this._applyPrefillAsync(tmpSeed, this._pendingPrefill);
			this._pendingPrefill = null;
		}
		tmpState.Sample = tmpSeed;
		await this._loadSpecScopeAsync(tmpSeed.IDProject);
	}

	/**
	 * @param {Record<string, any>} pSeed - The new sample, mutated.
	 * @param {{ IDProject?: number, IDLineItem?: number, IDMaterial?: number, SampleType?: string }} pPrefill
	 * @return {Promise<void>}
	 */
	async _applyPrefillAsync(pSeed, pPrefill)
	{
		for (const tmpKey of [ 'IDProject', 'IDLineItem', 'IDMaterial' ])
		{
			if (Number(pPrefill[tmpKey]) > 0)
			{
				pSeed[tmpKey] = Number(pPrefill[tmpKey]);
			}
		}
		if (pPrefill.SampleType)
		{
			const tmpTypes = await this._getJSON(`SampleTypes/FilteredTo/FBV~Hash~EQ~${encodeURIComponent(pPrefill.SampleType)}~FBV~Deleted~EQ~0/0/1`);
			if (Array.isArray(tmpTypes) && tmpTypes.length > 0)
			{
				pSeed.IDSampleType = tmpTypes[0].IDSampleType;
			}
		}
	}

	/**
	 * Scope the form's material pickers to the project's specification year before the form renders. A sample
	 * with no project clears the previous sample's scope. Fails open: the pickers show everything.
	 * @param {number} pIDProject
	 * @return {Promise<void>}
	 */
	async _loadSpecScopeAsync(pIDProject)
	{
		const tmpResolver = this.pict.providers.SpecScopeResolver;
		if (!tmpResolver || (typeof tmpResolver.loadForProject !== 'function'))
		{
			return;
		}
		try
		{
			await tmpResolver.loadForProject(parseInt(pIDProject, 10) || 0);
		}
		catch (pError)
		{
			this.pict.log.warn(`SampleWorkspace: specification-year scope did not load for project ${pIDProject}: ${(pError && pError.message) || pError}`);
		}
	}

	_resetState()
	{
		const tmpState = this.state;
		tmpState.Sample = {}; tmpState.LineItems = []; tmpState.Documents = []; tmpState.InaccessibleDocumentCount = 0; tmpState.InaccessibleDocumentIDs = []; tmpState.HiddenDocumentObservationJoins = null; tmpState.Cores = []; tmpState.Logs = [];
		tmpState.TestInstanceBundle = { TestInstances: [], TestMap: {}, TestInstanceDocumentMap: {} };
		tmpState.Message = ''; tmpState.Saving = false;
		this._resetTabLoads();
	}

	/** VIEW/EDIT-mode seed (called by the base loadAsync after the Sample record arrives). */
	async hydrateAsync(pRecord)
	{
		this._initialBundleGathers = new Map();
		this._locationTab.reset();
		this._listenForPanelMoves();
		this._idSample = pRecord.IDSample;
		this._guidSample = pRecord.GUIDSample || this._guidSample;
		const tmpState = this.state;
		tmpState.Sample = pRecord;
		tmpState.Mode = this._mode;
		this._resetTabLoads();
		tmpState.LineItems = []; tmpState.Documents = []; tmpState.InaccessibleDocumentCount = 0; tmpState.InaccessibleDocumentIDs = []; tmpState.HiddenDocumentObservationJoins = null; tmpState.Cores = [];
		tmpState.TestInstanceBundle = { TestInstances: [], TestMap: {}, TestInstanceDocumentMap: {} };
		await Promise.all([ this.ensureStatusVocabularyAsync(), this.loadFormAvailabilityAsync(), this._loadProjectPermissionsAsync(pRecord.IDProject) ]);
		if ((this._mode === 'edit') && this.isLockedForUser())
		{
			this._mode = 'view';
			tmpState.Mode = 'view';
			this._toast('This sample is locked, so its details can\'t be edited.', 'info');
			if ((typeof window !== 'undefined') && window.history && (typeof window.history.replaceState === 'function'))
			{
				window.history.replaceState(null, '', `#/Sample/Workspace/${parseInt(pRecord.IDSample, 10) || 0}`);
			}
		}
		if (this.isEditing)
		{
			await Promise.all([ this._loadReferenceBundleAsync(), this._loadSpecScopeAsync(pRecord.IDProject) ]);   // edit-form picklists (best-effort)
		}
		// Not awaited: the popovers only need these by the time someone hovers one.
		this._ensureSummaryManifestsAsync();
		// Logs are eager in LIMS (always loaded); do the same.
		tmpState.Logs = await this._loadLogsAsync();
	}

	/**
	 * Read what the current user is granted in the sample's project, so _canHash can count it.
	 * @param {number} pIDProject
	 * @return {Promise<void>}
	 */
	async _loadProjectPermissionsAsync(pIDProject)
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.loadCurrentUserProjectAsync !== 'function'))
		{
			return;
		}
		try
		{
			await tmpPermissions.loadCurrentUserProjectAsync(pIDProject);
		}
		catch (pError)
		{
			this.pict.log.warn(`SampleWorkspace: project ${pIDProject} permissions did not load: ${(pError && pError.message) || pError}`);
		}
	}

	/** Once per app: refresh when the panels' Scan Samples cart moves the sample on screen. */
	_listenForPanelMoves()
	{
		const tmpPanel = this.pict.views.QRPanel;
		if (this._removePanelMovesListener || !tmpPanel || (typeof tmpPanel.addMovedListener !== 'function'))
		{
			return;
		}
		this._removePanelMovesListener = tmpPanel.addMovedListener((pMove) => this.onSamplesMoved(pMove));
	}

	/**
	 * A move made outside the Location tab: refresh when it took the sample on screen.
	 * @param {{ Moved?: Array<Record<string, any>> }} pMove
	 * @return {Promise<void>}
	 */
	async onSamplesMoved(pMove)
	{
		const tmpIDSample = Number(this._idSample);
		if (!(tmpIDSample > 0) || !(pMove && Array.isArray(pMove.Moved) && pMove.Moved.some((pSample) => Number(pSample.IDSample) === tmpIDSample)))
		{
			return;
		}
		await this.refreshAfterMoveAsync();
	}

	/**
	 * Re-read what a move changes (the sample's status, its log and its location) and redraw. While the sample's
	 * details are being edited only the log and location are re-read, so the form isn't redrawn under the person.
	 * @return {Promise<void>}
	 */
	async refreshAfterMoveAsync()
	{
		const tmpIDSample = Number(this._idSample);
		const tmpEditing = this.isEditing;
		const [ tmpSample, tmpLogs ] = await Promise.all(
			[
				tmpEditing ? Promise.resolve(null) : this._getJSON(`Sample/${tmpIDSample}`),
				this._loadLogsAsync(),
				this._locationTab.loadAsync(),
			]);
		if (Number(this._idSample) !== tmpIDSample)
		{
			return;
		}
		if (tmpSample && (Number(tmpSample.IDSample) === tmpIDSample))
		{
			this._record = tmpSample;
			this.state.Sample = tmpSample;
		}
		if (Array.isArray(tmpLogs))
		{
			this.state.Logs = tmpLogs;
		}
		if (!tmpEditing)
		{
			// The header too: the status pill can change with the move.
			this.renderWorkspace();
		}
	}

	// --- per-tab lazy loads ----------------------------------------------------------------------------------
	/** Forget every tab's data; a load still in flight for the previous sample is discarded when it lands. */
	_resetTabLoads()
	{
		this._activatedTabs = {};
		this._tabLoads = {};
	}

	/**
	 * @param {string} pTab
	 * @return {{ Promise: Promise<void>|null, Request: number, Loaded: boolean, Error: string }}
	 */
	_tabLoad(pTab)
	{
		if (!this._tabLoads)
		{
			this._tabLoads = {};
		}
		if (!this._tabLoads[pTab])
		{
			this._tabLoads[pTab] = { Promise: null, Request: 0, Loaded: false, Error: '' };
		}
		return this._tabLoads[pTab];
	}

	/**
	 * Mark a tab's data stale so the next _ensureTabDataLoaded fetches it again. The data already shown stays until the
	 * new fetch lands, and a fetch still in flight is superseded.
	 * @param {string} pTab
	 */
	_invalidateTab(pTab)
	{
		this._activatedTabs[pTab] = false;
		this._tabLoad(pTab).Promise = null;
	}

	/**
	 * Load a tab's data once per sample. Callers that arrive while the fetch is running share it.
	 * @param {string} pTab
	 * @return {Promise<void>}
	 */
	_ensureTabDataLoaded(pTab)
	{
		if (this._activatedTabs[pTab])
		{
			return Promise.resolve();
		}
		const tmpLoad = this._tabLoad(pTab);
		if (!tmpLoad.Promise)
		{
			tmpLoad.Promise = this._loadTabDataAsync(pTab, tmpLoad);
		}
		return tmpLoad.Promise;
	}

	/**
	 * @param {string} pTab
	 * @param {{ Promise: Promise<void>|null, Request: number, Loaded: boolean, Error: string }} pLoad
	 * @return {Promise<void>}
	 */
	async _loadTabDataAsync(pTab, pLoad)
	{
		pLoad.Request++;
		const tmpRequest = pLoad.Request;
		const fIsCurrent = () => (this._tabLoads[pTab] === pLoad) && (pLoad.Request === tmpRequest);
		try
		{
			const tmpData = await this._fetchTabDataAsync(pTab, this._idSample);
			if (!fIsCurrent())
			{
				return;
			}
			Object.assign(this.state, tmpData);
			pLoad.Loaded = true;
			pLoad.Error = '';
			this._activatedTabs[pTab] = true;
		}
		catch (pError)
		{
			const tmpMessage = (pError && pError.message) || String(pError);
			this.pict.log.error(`SampleWorkspace: tab '${pTab}' load failed: ${tmpMessage}`);
			if (fIsCurrent())
			{
				pLoad.Error = tmpMessage;
			}
		}
		finally
		{
			if (fIsCurrent())
			{
				pLoad.Promise = null;
			}
		}
	}

	/**
	 * @param {string} pTab
	 * @param {number} pIDSample
	 * @return {Promise<Record<string, any>>} the state fields the tab renders from
	 */
	async _fetchTabDataAsync(pTab, pIDSample)
	{
		if (pTab === 'lineitems')
		{
			const tmpLineItemProvider = this.pict.providers.LineItemProvider;
			return {
				LineItems: (tmpLineItemProvider && typeof tmpLineItemProvider.getLineItemsByIDSample === 'function')
					? (await tmpLineItemProvider.getLineItemsByIDSample(pIDSample)) || []
					: [],
			};
		}
		if (pTab === 'testinstances')
		{
			const tmpSamples = this.pict.fable.HeadlightSamples;
			if (!tmpSamples)
			{
				throw new Error('The samples provider is not available.');
			}
			const tmpScope = tmpSamples.forSample(pIDSample);
			tmpScope.invalidate();
			return { TestInstanceBundle: await tmpScope.getTestBundle() };
		}
		if (pTab === 'documents')
		{
			const tmpDocProvider = this.pict.providers.DocumentProvider;
			const [ tmpDocuments, tmpMedia ] = await Promise.all(
				[
					(tmpDocProvider && typeof tmpDocProvider.getDocumentsByRemoteID === 'function')
						? tmpDocProvider.getDocumentsByRemoteID(pIDSample, 'Sample')
						: [],
					this._getJSON(`Sample/${pIDSample}/DocumentMedia`),
				]);
			// The sample's own media route names documents the reader cannot open; a reader's DocumentPolyJoin read does not.
			const tmpHasMedia = !!(tmpMedia && Array.isArray(tmpMedia.Documents) && Array.isArray(tmpMedia.ObservationJoins));
			const tmpJoins = tmpHasMedia
				? tmpMedia.Documents.map((pIDDocument) => ({ IDDocument: pIDDocument }))
				: await this._getJSON(`DocumentPolyJoins/FilteredTo/FBV~IDRemote~EQ~${pIDSample}~FBV~RemoteType~EQ~Sample/0/1000`);
			const tmpHiddenIDs = this._inaccessibleDocumentIDs(tmpJoins, tmpDocuments || []);
			return {
				Documents: tmpDocuments || [],
				InaccessibleDocumentCount: tmpHiddenIDs.length,
				InaccessibleDocumentIDs: tmpHiddenIDs,
				HiddenDocumentObservationJoins: tmpHasMedia ? tmpMedia.ObservationJoins.filter((pJoin) => tmpHiddenIDs.indexOf(Number(pJoin.IDDocument)) > -1) : null,
			};
		}
		if (pTab === 'cores')
		{
			return { Cores: (await this._getJSON(`Cores/FilteredTo/FBV~IDSample~EQ~${pIDSample}~FBV~Deleted~EQ~0/0/500`)) || [] };
		}
		return {};
	}

	/**
	 * Fetch a tab whose load failed again, from its Try again button.
	 * @param {string} pTab
	 * @return {Promise<void>}
	 */
	async retryTab(pTab)
	{
		this._invalidateTab(pTab);
		this._tabLoad(pTab).Error = '';
		this.repaintBody();
		await this._ensureTabDataLoaded(pTab);
		this.repaintBody();
	}

	/**
	 * Until a tab's data has arrived, paint a loading state in its place, or the load's error with Try again, so an
	 * empty list never shows before the fetch answers.
	 * @param {string} pTab
	 * @param {string} pNoun - what the tab lists, e.g. 'tests'
	 * @return {boolean} true when the tab body was painted here and the renderer should stop
	 */
	_renderTabPending(pTab, pNoun)
	{
		const tmpLoad = this._tabLoad(pTab);
		if (tmpLoad.Loaded)
		{
			return false;
		}
		const tmpBody = document.querySelector('#SampleWorkspace-Body');
		if (!tmpBody)
		{
			return true;
		}
		const tmpState = document.createElement('div');
		tmpState.className = 'sw-state';
		if (tmpLoad.Error)
		{
			const tmpIcon = document.createElement('i');
			tmpIcon.className = 'fas fa-triangle-exclamation has-text-danger mr-2';
			const tmpMessage = document.createElement('span');
			tmpMessage.textContent = `Could not load this sample's ${pNoun}: ${tmpLoad.Error}`;
			const tmpRetry = document.createElement('button');
			tmpRetry.className = 'button is-small is-link is-soft mt-3';
			tmpRetry.textContent = 'Try again';
			tmpRetry.addEventListener('click', () => this.retryTab(pTab));
			const tmpActions = document.createElement('div');
			tmpActions.append(tmpRetry);
			tmpState.append(tmpIcon, tmpMessage, tmpActions);
		}
		else
		{
			const tmpSpinner = document.createElement('span');
			tmpSpinner.className = 'pw-spinner pw-spinner-inline';
			tmpState.append(tmpSpinner, ` Loading ${pNoun}…`);
		}
		tmpBody.innerHTML = '';
		tmpBody.append(tmpState);
		return true;
	}

	async _loadLogsAsync()
	{
		try
		{
			const tmpLogProvider = this.pict.providers.SampleLogProvider;
			if (tmpLogProvider && typeof tmpLogProvider.getSampleLogsByIDSample === 'function')
			{
				return (await tmpLogProvider.getSampleLogsByIDSample(this._idSample)) || [];
			}
			return (await this._getJSON(`SampleLogs/FilteredTo/FBV~IDSample~EQ~${this._idSample}~FSF~CreateDate~DESC~0/0/500`)) || [];
		}
		catch (pError) { return []; }
	}

	// --- configuration-based form (manifest-packaging-service + form-host) -----------------------------------
	/**
	 * The Sample record manifest for a verb, fetched once and shared by every caller, including those that ask while
	 * the fetch is still in flight (the form mounts several times as a sample opens).
	 * @param {string} pVerb - Create, Edit or View
	 * @return {Promise<Record<string, any>>}
	 */
	_loadManifestAsync(pVerb)
	{
		if (!this._manifestsByVerb[pVerb])
		{
			this._manifestsByVerb[pVerb] = this._fetchManifestAsync(pVerb);
		}
		return this._manifestsByVerb[pVerb];
	}

	async _fetchManifestAsync(pVerb)
	{
		let tmpManifest = null;
		try
		{
			const tmpService = this.pict.services.ManifestPackagingService || this.pict.ManifestPackagingService;
			if (tmpService && typeof tmpService.loadRecordManifestSchema === 'function')
			{
				tmpManifest = await tmpService.loadRecordManifestSchema('Sample', pVerb, LIMS_PRODUCT);
			}
		}
		catch (pError)
		{
			this.pict.log.error(`SampleWorkspace: manifest load (${pVerb}) failed: ${(pError && pError.message) || pError}`);
		}
		if (!tmpManifest)
		{
			tmpManifest = (pVerb === 'Create') ? DEFAULT_SAMPLE_CREATE_MANIFEST
				: (pVerb === 'Edit') ? DEFAULT_SAMPLE_EDIT_MANIFEST
					: DEFAULT_SAMPLE_VIEW_MANIFEST;
		}
		return tmpManifest;
	}

	// Load the shared reference bundle the create/edit picklists need (Containers/States/Cities/Countries/Locations)
	// into the AppData addresses the manifests reference. Best-effort; read-only view mode does not need it.
	async _loadReferenceBundleAsync()
	{
		if (this._referenceBundleLoaded) { return; }
		this._referenceBundleLoaded = true;
		const tmpApp = this.pict.AppData;
		const tmpMap = (pList, pKey, pVal) => (Array.isArray(pList) ? pList.map((pRow) => ({ Hash: pRow[pKey], Value: pRow[pVal] || pRow[pKey] })) : []);
		try
		{
			const [ tmpContainers, tmpCountries, tmpStates, tmpLocations ] = await Promise.all(
			[
				this._getJSON('ListDefaults/FilteredTo/FBV~NamespaceKey~EQ~Misc~FBV~NamespaceValue~EQ~Container/0/500'),
				this._getJSON('ListDefaults/FilteredTo/FBV~NamespaceKey~EQ~Geographical~FBV~NamespaceValue~EQ~Country/0/500'),
				this._getJSON('ListDefaults/FilteredTo/FBV~NamespaceKey~EQ~Geographical~FBV~NamespaceValue~EQ~State/0/500'),
				this.pict.providers.LocationProvider.getAllLocations().catch(() => []),
			]);
			tmpApp.Containers = tmpMap(tmpContainers, 'Hash', 'Name');
			tmpApp.SourceCountries = tmpMap(tmpCountries, 'Hash', 'Name');
			tmpApp.SourceStates = tmpMap(tmpStates, 'Hash', 'Name');
			tmpApp.SourceCities = tmpApp.SourceCities || [];
			tmpApp.Locations = Array.isArray(tmpLocations) ? tmpLocations : [];
		}
		catch (pError) { /* picklists degrade to empty; form still renders */ }
	}

	/**
	 * Gather the manifest's form-level InitialBundle through the LIMS FormInitialBundle provider, as the LIMS Sample
	 * views do: once per opened sample (the hydrate hooks start a new set), however many times the form mounts
	 * meanwhile. A failed gather is logged, the form still opens, and the next mount tries again.
	 * @param {Record<string, any>} pManifest - the Sample record manifest about to be loaded
	 * @return {Promise<void>}
	 */
	async _gatherInitialBundleAsync(pManifest)
	{
		const tmpGatherer = this.pict.providers.FormInitialBundle;
		if (!tmpGatherer || !pManifest || !Array.isArray(pManifest.InitialBundle) || (pManifest.InitialBundle.length < 1))
		{
			return;
		}
		const tmpGathers = this._initialBundleGathers;
		if (!tmpGathers.has(pManifest))
		{
			let fResolve;
			tmpGathers.set(pManifest, new Promise((pResolve) =>
			{
				fResolve = pResolve;
			}));
			const fDone = (pError) =>
			{
				if (pError)
				{
					this.pict.log.error(`SampleWorkspace: InitialBundle gather failed: ${(pError && pError.message) || pError}`);
					tmpGathers.delete(pManifest);
				}
				fResolve();
			};
			try
			{
				tmpGatherer.gatherInitialBundle(pManifest, fDone);
			}
			catch (pError)
			{
				fDone(pError);
			}
		}
		await tmpGathers.get(pManifest);
	}

	/** Mount the config-driven form for the current mode into #Sample-FormHost (called by the view after it paints). */
	async mountFormAsync()
	{
		const tmpHost = this.formHost;
		if (!tmpHost || (typeof tmpHost.loadForm !== 'function'))
		{
			this.state.Message = 'The Sample form runner is not available.';
			return;
		}
		const tmpVerb = (this._mode === 'create') ? 'Create' : (this._mode === 'edit') ? 'Edit' : 'View';
		const tmpManifest = await this._loadManifestAsync(tmpVerb);
		await this._gatherInitialBundleAsync(tmpManifest);
		// Seed the marshal target: the form binds to AppData.SampleDetails.* (DataAddress 'AppData').
		this.pict.AppData.SampleDetails = (this._mode === 'create')
			? Object.assign({ Extension: {} }, this.state.Sample || {})
			: Object.assign({ Extension: {} }, this.state.Sample || {});
		try
		{
			const tmpMetacontroller = this.pict.views.PictFormMetacontroller;
			if (tmpMetacontroller && typeof tmpMetacontroller.clearManifestDescription === 'function')
			{
				tmpMetacontroller.clearManifestDescription();
			}
			this._applyFormTheme();
			tmpHost.loadForm(tmpManifest, { DestinationAddress: '#Sample-FormHost', DataAddress: 'AppData' });
			// Rebuild picklists declared by the manifest (create/edit).
			const tmpMetaLists = this.pict.providers.DynamicMetaLists;
			if (tmpMetaLists && Array.isArray(tmpManifest.PickLists))
			{
				for (const tmpPickList of tmpManifest.PickLists)
				{
					if (tmpPickList && tmpPickList.Hash && typeof tmpMetaLists.rebuildListByHash === 'function')
					{
						try { tmpMetaLists.rebuildListByHash(tmpPickList.Hash); } catch (pErr) { /* best-effort */ }
					}
				}
			}
			// The form renders asynchronously (Select2 + entity pickers resolve late), so pushing the data into the
			// inputs has to happen AFTER that render settles — a single immediate marshal leaves the plain text/date
			// inputs blank. Marshal on the next tick and again shortly after to catch the late-resolving pickers, then
			// apply the read-only treatment for view mode (the LIMS Read theme renders these default inputs read-only).
			const fnFinalize = () =>
			{
				if (typeof tmpHost.marshalToView === 'function') { try { tmpHost.marshalToView(); } catch (pErr) { /* best-effort */ } }
				if (this._mode === 'view') { this._applyViewReadOnly(); }
			};
			setTimeout(fnFinalize, 0);
			setTimeout(fnFinalize, 250);
		}
		catch (pError)
		{
			this.state.Message = `Could not open the Sample form: ${(pError && pError.message) ? pError.message : pError}`;
			if (this.view) { this.repaintBody(); }
		}
	}

	// --- actions ---------------------------------------------------------------------------------------------
	goToEdit()
	{
		if (!this._guidSample) { return; }
		window.location.hash = `#/PSRS/Sample/Edit/${this._guidSample}`;
	}
	goToClone()
	{
		if (!this._guidSample) { return; }
		window.location.hash = `#/PSRS/Sample/Create/ClonedFrom/${this._guidSample}`;
	}
	goToView()
	{
		if (this._guidSample) { window.location.hash = `#/PSRS/Sample/View/${this._guidSample}`; }
		else { this.backToList(); }
	}
	cancelForm()
	{
		const tmpHost = this.formHost;
		if (tmpHost && typeof tmpHost.destroy === 'function') { try { tmpHost.destroy(); } catch (pError) { /* best-effort */ } }
		if (this._mode === 'create') { this.backToList(); }
		else { this.goToView(); }
	}

	/** Save the config-driven form. Create -> ported SampleProvider.createSample (QR + log + location join); Edit -> updateSample. */
	async saveForm()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		const tmpHost = this.formHost;
		const tmpSampleProvider = this.pict.providers.SampleProvider;
		if (!tmpSampleProvider) { this._toast('The Sample save provider is not available.', 'error'); return; }
		if (!this.canCreate() && this._mode === 'create') { this._toast('You do not have permission to create samples.', 'error'); return; }
		if (!this.canEditForm() && this._mode === 'edit') { this._toast('You do not have permission to edit this sample.', 'error'); return; }
		if (tmpHost && typeof tmpHost.save === 'function') { try { tmpHost.save(); } catch (pError) { /* marshal best-effort */ } }
		const tmpSample = Object.assign({}, this.pict.AppData.SampleDetails || {});
		tmpState.Saving = true;
		try
		{
			let tmpResult;
			if (this._mode === 'create')
			{
				delete tmpSample.CreatingIDUser;
				tmpResult = await tmpSampleProvider.createSample(tmpSample, null, true);
			}
			else
			{
				tmpResult = await tmpSampleProvider.updateSample(tmpSample);
			}
			if (tmpResult && (tmpResult.IDSample || tmpResult.GUIDSample))
			{
				this._toast(this._mode === 'create' ? 'Sample created.' : 'Sample saved.', 'success');
				this._guidSample = tmpResult.GUIDSample || this._guidSample;
				this._manifestsByVerb = {};
				if (tmpHost && typeof tmpHost.destroy === 'function') { try { tmpHost.destroy(); } catch (pErr) { /* best-effort */ } }
				window.location.hash = `#/PSRS/Sample/View/${this._guidSample}`;
			}
			else
			{
				this._toast('The sample could not be saved (it may be locked).', 'error');
			}
		}
		catch (pError)
		{
			this._toast(`Save failed: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
		finally
		{
			tmpState.Saving = false;
		}
	}

	async deleteSample()
	{
		const tmpSample = this.state.Sample || {};
		if (!tmpSample.IDSample) { return; }
		if (!this.canDelete()) { this._toast('You do not have permission to delete this sample.', 'error'); return; }
		if (this.isLockedForUser()) { this._toast('This sample is locked and cannot be deleted.', 'error'); return; }
		const tmpSampleProvider = this.pict.providers.SampleProvider;
		try
		{
			if (tmpSampleProvider && typeof tmpSampleProvider.deleteSample === 'function')
			{
				await tmpSampleProvider.deleteSample(tmpSample.IDSample);
			}
			else
			{
				await new Promise((resolve) => this.restClient.deleteEntity('Sample', tmpSample.IDSample, () => resolve()));
			}
			this._toast('Sample deleted.', 'success');
			this.backToList();
		}
		catch (pError)
		{
			this._toast(`Delete failed: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
	}

	/**
	 * Pick the form theme for this mode before the form renders: the Read theme draws every field as display text
	 * (entity names resolved, no pickers), the Edit theme draws inputs. The theme is app-wide and other screens
	 * change it (the Samples list filter switches to Edit), so it has to be set here, as legacy LIMS did.
	 */
	_applyFormTheme()
	{
		const tmpApp = this.app;
		if (tmpApp && (typeof tmpApp.changeTheme === 'function'))
		{
			tmpApp.changeTheme((this._mode === 'view') ? 'Read' : 'Edit');
		}
	}

	/** View mode is read-only: lock the rendered form so the customer's View-manifest fields display (entities resolved) but cannot be edited. */
	_applyViewReadOnly()
	{
		try
		{
			const tmpHost = document.querySelector('#Sample-FormHost');
			if (!tmpHost) { return; }
			tmpHost.classList.add('sw-form-readonly');
			const tmpInputs = tmpHost.querySelectorAll('input, textarea');
			for (let i = 0; i < tmpInputs.length; i++) { tmpInputs[i].setAttribute('readonly', 'readonly'); tmpInputs[i].setAttribute('tabindex', '-1'); }
		}
		catch (pError) { /* decorative; ignore */ }
	}

	/**
	 * Print the Sample label: the same label LIMS prints, on this device's label stock.
	 * @param {boolean} [pShowOptions] - Open Print options (label stock and preview) instead of printing right away.
	 */
	async printLabel(pShowOptions)
	{
		const tmpSample = this.state.Sample || {};
		const tmpQR = this.pict.providers.QRCodeProvider;
		if (!tmpQR || !tmpSample.QRCode)
		{
			this._toast('QR label printing is not available for this sample.', 'error');
			return;
		}
		// The customer's label layout (LIMS-Sample-Printable); this app doesn't load it at startup as LIMS does.
		await tmpQR.ensurePrintableManifests();
		// printDecoratedEntity answers null only when the label couldn't be built.
		if (await tmpQR.printDecoratedEntity('Sample', tmpSample.QRCode, undefined, undefined, tmpSample, { ShowOptions: !!pShowOptions }) === null)
		{
			this._toast('The label could not be printed. Check your connection and try again.', 'error');
		}
	}

	/** Render the QR thumbnail into a container element (the view calls this after it paints the header). */
	renderQRThumbnail(pContainerSelector)
	{
		const tmpSample = this.state.Sample || {};
		const tmpQR = this.pict.providers.HeadlightQR;
		if (!tmpQR || !tmpSample.QRCode)
		{
			return;
		}
		try
		{
			const tmpContainer = document.querySelector(pContainerSelector);
			if (!tmpContainer)
			{
				return;
			}
			tmpContainer.innerHTML = tmpQR.renderSVG(tmpQR.encode('Sample', String(tmpSample.QRCode)), { SizePX: 128, Title: `QR ${tmpSample.QRCode}` });
		}
		catch (pError)
		{
			// The thumbnail is decorative.
		}
	}

	// --- Line-item mutations (LineItemProvider.saveSampleLineItemJoin/deleteSampleLineItemJoin + qty rollup) ------
	get lineItemAddState()
	{
		if (!this.state.LineItemAdd)
		{
			this.state.LineItemAdd = { Open: false, IDProject: 0, IDLineItem: 0, Quantity: 0 };
		}
		return this.state.LineItemAdd;
	}

	async _reloadLineItemsAsync()
	{
		const tmpLineItemProvider = this.pict.providers.LineItemProvider;
		this.state.LineItems = (tmpLineItemProvider && typeof tmpLineItemProvider.getLineItemsByIDSample === 'function')
			? (await tmpLineItemProvider.getLineItemsByIDSample(this._idSample)) || []
			: [];
	}

	/**
	 * The sample's represented quantity: the sum of its line items' represented quantities.
	 * @param {Array<Record<string, any>>} pLineItems - the sample's line items, each carrying its join's RepresentedQuantity
	 * @return {number}
	 */
	static sumRepresentedQuantity(pLineItems)
	{
		let tmpQuantity = 0;
		for (const tmpLI of (pLineItems || []))
		{
			tmpQuantity += Number(tmpLI && tmpLI.RepresentedQuantity) || 0;
		}
		return tmpQuantity;
	}

	/**
	 * Recompute Sample.RepresentedQuantity = sum of the line-item join quantities, then PUT Sample/Upsert (the LIMS rollup
	 * invariant). Throws when the sample can't be saved, so the caller says the total did not update.
	 * @return {Promise<void>}
	 */
	async _updateSampleQuantityFromLineItemsAsync()
	{
		const tmpSampleProvider = this.pict.providers.SampleProvider;
		if (!tmpSampleProvider || (typeof tmpSampleProvider.updateSample !== 'function'))
		{
			throw new Error('The sample save provider is not available.');
		}
		const tmpQuantity = SampleWorkspaceProvider.sumRepresentedQuantity(this.state.LineItems);
		const tmpSaved = await tmpSampleProvider.updateSample({ IDSample: this.state.Sample.IDSample, GUIDSample: this.state.Sample.GUIDSample, RepresentedQuantity: tmpQuantity });
		this.state.Sample.RepresentedQuantity = (tmpSaved && (tmpSaved.RepresentedQuantity !== undefined)) ? tmpSaved.RepresentedQuantity : tmpQuantity;
	}

	/**
	 * After a line item change: reload the list and roll the total up onto the sample, telling the user if the total
	 * could not be saved (the line item change itself already went through).
	 * @param {string} pDoneMessage
	 * @return {Promise<void>}
	 */
	async _afterLineItemChangeAsync(pDoneMessage)
	{
		await this._reloadLineItemsAsync();
		try
		{
			await this._updateSampleQuantityFromLineItemsAsync();
			this._toast(pDoneMessage, 'success');
		}
		catch (pError)
		{
			this._toast(`${pDoneMessage} The sample's represented quantity could not be updated: ${(pError && pError.message) || pError}`, 'error');
		}
		if (this.view) { this.renderWorkspace(); }
	}

	toggleLineItemAdd()
	{
		const tmpAdd = this.lineItemAddState;
		tmpAdd.Open = !tmpAdd.Open;
		tmpAdd.IDLineItem = 0;
		tmpAdd.Quantity = 0;
		// Start from the sample's own project; the user can pick another.
		tmpAdd.IDProject = (tmpAdd.Open && this.state.Sample && this.state.Sample.IDProject) ? Number(this.state.Sample.IDProject) : 0;
		if (this.view) { this.repaintBody(); }
	}

	/**
	 * The add form's Project picker changed: the Line Item picker lists that project's line items.
	 * @param {any} pIDProject
	 */
	selectLineItemProject(pIDProject)
	{
		const tmpAdd = this.lineItemAddState;
		const tmpID = parseInt(pIDProject, 10) || 0;
		if (tmpID === Number(tmpAdd.IDProject))
		{
			return;
		}
		tmpAdd.IDProject = tmpID;
		tmpAdd.IDLineItem = 0;
		this._mountLineItemPicker();
	}

	async addLineItem()
	{
		const tmpAdd = this.lineItemAddState;
		if (!this.canUpdate()) { this._toast('You do not have permission to modify this sample.', 'error'); return; }
		if (this.isLockedForUser())
		{
			this._toastLocked();
			return;
		}
		if (!Number(tmpAdd.IDLineItem)) { this._toast('Select a line item to add.', 'error'); return; }
		const tmpLineItemProvider = this.pict.providers.LineItemProvider;
		try
		{
			await tmpLineItemProvider.saveSampleLineItemJoin({ IDSample: this._idSample, IDLineItem: Number(tmpAdd.IDLineItem), RepresentedQuantity: Number(tmpAdd.Quantity) || 0 });
		}
		catch (pError)
		{
			this._toast(`Could not add line item: ${(pError && pError.message) || pError}`, 'error');
			return;
		}
		tmpAdd.Open = false; tmpAdd.IDLineItem = 0; tmpAdd.Quantity = 0;
		await this._afterLineItemChangeAsync('Line item added.');
	}

	/**
	 * Whether the user may change the sample's line items and tests; tells them why not when they can't.
	 * @return {boolean}
	 */
	_guardChangeSample()
	{
		if (!this.canUpdate())
		{
			this._toast('You do not have permission to modify this sample.', 'error');
			return false;
		}
		if (this.isLockedForUser())
		{
			this._toastLocked();
			return false;
		}
		return true;
	}

	async editLineItemQuantity(pIDJoin, pIDLineItem, pQuantity)
	{
		if (!this._guardChangeSample())
		{
			return;
		}
		const tmpLineItemProvider = this.pict.providers.LineItemProvider;
		const tmpJoin = (this.state.LineItems || []).find((pLI) => Number(pLI.IDSampleLineItemJoin) === Number(pIDJoin)) || {};
		const tmpPayload = { IDSampleLineItemJoin: Number(pIDJoin), IDSample: this._idSample, IDLineItem: Number(pIDLineItem), RepresentedQuantity: Number(pQuantity) || 0 };
		// Upsert by GUID as legacy LIMS did, so the existing join is updated rather than a second one created.
		if (tmpJoin.GUIDSampleLineItemJoin)
		{
			tmpPayload.GUIDSampleLineItemJoin = tmpJoin.GUIDSampleLineItemJoin;
		}
		try
		{
			await tmpLineItemProvider.saveSampleLineItemJoin(tmpPayload);
		}
		catch (pError)
		{
			this._toast(`Could not update quantity: ${(pError && pError.message) || pError}`, 'error');
			return;
		}
		await this._afterLineItemChangeAsync('Quantity updated.');
	}

	async removeLineItem(pIDJoin)
	{
		if (!this.canUpdate()) { this._toast('You do not have permission to modify this sample.', 'error'); return; }
		if (this.isLockedForUser())
		{
			this._toastLocked();
			return;
		}
		const tmpLineItemProvider = this.pict.providers.LineItemProvider;
		try
		{
			await tmpLineItemProvider.deleteSampleLineItemJoin(Number(pIDJoin));
		}
		catch (pError)
		{
			this._toast(`Could not remove line item: ${(pError && pError.message) || pError}`, 'error');
			return;
		}
		await this._afterLineItemChangeAsync('Line item removed.');
	}

	// --- Test instances (reuse the shared TestInstanceWizard panel — already registered by HeadlightPanels) --------
	async openTestWizard()
	{
		if (!this.canUpdate()) { this._toast('You do not have permission to modify this sample.', 'error'); return; }
		if (this.isLockedForUser())
		{
			this._toastLocked();
			return;
		}
		const tmpWizard = this.pict.views.TestInstanceWizard;
		if (!tmpWizard || (typeof tmpWizard.openTestInstanceWizard !== 'function')) { this._toast('The test wizard is not available.', 'error'); return; }
		// The wizard reads the sample from AppData.SampleDetails and offers the sample's unlocked documents
		// (AppData.ActiveLIMSSampleDocuments) to add the tests to. Never register a stand-in for the legacy
		// LIMSSampleRecordView: pict walks every registered view on solve and marshal, and a plain object there breaks
		// every form rendered afterwards (the Details tab looped and crashed the page).
		await this._ensureTabDataLoaded('documents');
		this.pict.AppData.SampleDetails = Object.assign({}, this.state.Sample || {});
		this.pict.AppData.ActiveLIMSSampleDocuments = this.state.Documents || [];
		try { tmpWizard.openTestInstanceWizard({ onTestsCreated: () => this._afterTestsCreated() }); }
		catch (pError) { this._toast(`Could not open the test wizard: ${(pError && pError.message) || pError}`, 'error'); }
	}

	/**
	 * The wizard saved new tests (and maybe new documents): reload both tabs' data.
	 * @return {Promise<void>}
	 */
	async _afterTestsCreated()
	{
		this._invalidateTab('documents');
		await this.refreshTestInstances();
	}

	/**
	 * Delete one of this sample's tests, after the user confirms.
	 * @param {number} pIDTestInstance
	 * @return {Promise<void>}
	 */
	async deleteTestInstance(pIDTestInstance)
	{
		if (!this.canUpdate() || !this._canHash('TestInstance-Delete'))
		{
			this._toast('You do not have permission to delete tests.', 'error');
			return;
		}
		if (this.isLockedForUser())
		{
			this._toastLocked();
			return;
		}
		const tmpTestProvider = this.pict.providers.TestInstanceProvider;
		if (!(pIDTestInstance > 0) || !tmpTestProvider || (typeof tmpTestProvider.deleteTestInstanceByID !== 'function'))
		{
			return;
		}
		const tmpModal = this.pict.views && this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || (typeof tmpModal.confirm !== 'function'))
		{
			this._toast('Could not ask to confirm the delete, so the test was kept.', 'error');
			return;
		}
		if (!(await tmpModal.confirm('Delete this test from the sample?', { title: 'Delete test', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true })))
		{
			return;
		}
		try
		{
			await tmpTestProvider.deleteTestInstanceByID(pIDTestInstance);
			this._toast('Test deleted.', 'success');
		}
		catch (pError)
		{
			this._toast(`Could not delete the test: ${(pError && pError.message) || pError}`, 'error');
		}
		await this.refreshTestInstances();
	}

	async refreshTestInstances()
	{
		this._invalidateTab('testinstances');
		await this._ensureTabDataLoaded('testinstances');
		if (this.view) { this.renderWorkspace(); }
	}

	// --- Documents (reuse the shared HeadlightPaneDocuments panel) -------------------------------------------
	attachDocument(pCreateMode)
	{
		if (!this.canUpdate()) { this._toast('You do not have permission to modify this sample.', 'error'); return; }
		const tmpPane = this.pict.views.HeadlightPaneDocuments;
		const tmpPanels = this.pict.views.HeadlightPanels;
		if (!tmpPane || !tmpPanels) { this._toast('The document panel is not available.', 'error'); return; }
		this.pict.AppData.SampleDetails = this.state.Sample || {};
		tmpPane.Intent =
		{
			type: 'add',
			handler: (pIDDocument) => { this._documentAttachHandler(pIDDocument); },
			afterRenderHandler: () =>
			{
				const tmpSample = this.state.Sample || {};
				if (tmpSample.IDProject && this.pict.views.DocumentCreate && this.pict.views.DocumentCreate.CurrentDocumentRecord)
				{
					this.pict.views.DocumentCreate.CurrentDocumentRecord.Project = tmpSample.IDProject;
				}
			},
		};
		tmpPanels.handlePanelToggle('Documents', 'open');
		if (pCreateMode && (typeof tmpPanels.toggleTabContent === 'function'))
		{
			try { tmpPanels.toggleTabContent('HeadlightPanels-DocumentPanel-Create', 'Document'); } catch (pError) { /* best-effort */ }
		}
	}

	async _documentAttachHandler(pIDDocument)
	{
		try
		{
			await this.pict.providers.DocumentProvider.addRemoteToDocument(pIDDocument, this._idSample, 'Sample');
			try { this.pict.views.HeadlightPanels.handlePanelToggle('Documents', 'close'); } catch (pError) { /* best-effort */ }
			this._invalidateTab('documents');
			await this._ensureTabDataLoaded('documents');
			this._toast('Document attached.', 'success');
			if (this.view) { this.renderWorkspace(); }
		}
		catch (pError) { this._toast(`Could not attach document: ${(pError && pError.message) || pError}`, 'error'); }
	}

	async addLog()
	{
		const tmpState = this.state;
		const tmpNotes = (tmpState.NewLogNotes || '').trim();
		if (!tmpNotes || !this._idSample) { return; }
		const tmpLogProvider = this.pict.providers.SampleLogProvider;
		try
		{
			if (tmpLogProvider && typeof tmpLogProvider.addNote === 'function')
			{
				// Read fresh: the sample may have moved since the Location tab last loaded.
				await this._locationTab.loadAsync();
				const tmpIDLocation = Number((this._locationTab.state.Location || {}).IDLocation) || 0;
				await tmpLogProvider.addNote(this._idSample, tmpNotes, { IDLocation: tmpIDLocation });
			}
			tmpState.NewLogNotes = '';
			tmpState.Logs = await this._loadLogsAsync();
			this._toast('Log added.', 'success');
			if (this.view) { this.repaintBody(); }
		}
		catch (pError)
		{
			this._toast(`Could not add log: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
	}

	// --- Body renderers (ported from the old SampleWorkspace view; painted by the base `render` dispatch) --------

	/** Details tab (view) + the edit/create body: mount the config-driven manifest form into #Sample-FormHost. */
	renderForm()
	{
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', '<div id="Sample-FormHost" class="sw-form-host"></div>');
		this.mountFormAsync();
	}

	_tabActionsHTML(pInnerHTML) { return `<div class="sw-toolbar">${pInnerHTML}</div>`; }
	_empty(pText) { return `<div class="sw-empty"><i class="fas fa-inbox"></i><p>${this._esc(pText)}</p></div>`; }

	_statusClass(pCode)
	{
		const tmpCode = String(pCode || '').toUpperCase();
		if (tmpCode === 'PASS' || tmpCode === 'PASC') { return 'is-pass'; }
		if (tmpCode === 'FAIL' || tmpCode === 'FAIC') { return 'is-fail'; }
		if (tmpCode === 'VOID') { return 'is-void'; }
		if (tmpCode === 'PEND' || tmpCode === 'TEST' || tmpCode === 'TSTR') { return 'is-pending'; }
		return 'is-neutral';
	}
	_tiStatusClass(pStatus)
	{
		const tmpS = String(pStatus || '').toLowerCase();
		if (tmpS.indexOf('pass') > -1 || tmpS.indexOf('sqty') > -1) { return 'is-pass'; }
		if (tmpS.indexOf('fail') > -1) { return 'is-fail'; }
		return 'is-neutral';
	}

	/**
	 * @param {String} pTone - a SampleTest-Rules tone ('success' | 'danger' | ...)
	 * @return {String} the sw-chip class for it
	 */
	_toneClass(pTone)
	{
		if (pTone === 'success')
		{
			return 'is-pass';
		}
		if (pTone === 'danger')
		{
			return 'is-fail';
		}
		return 'is-neutral';
	}

	/**
	 * The marker beside a value a form holds until it is approved, or '' when the value is the test's own.
	 * @param {?Object} pDocument - the unapproved document the value comes from
	 * @return {String}
	 */
	_tiPendingHTML(pDocument)
	{
		if (!pDocument)
		{
			return '';
		}
		const tmpName = pDocument.Name || `Document #${pDocument.IDDocument}`;
		return ` <span class="sw-ti-pending" title="${this._esc(`From ${tmpName}. Saved to the test when the form is approved.`)}">(pending approval)</span>`;
	}

	_fmtDate(pDate)
	{
		if (!pDate) { return ''; }
		const tmpStr = String(pDate);
		return (tmpStr.indexOf('T') > -1) ? tmpStr.slice(0, 10) : tmpStr;
	}

	/** A date and time the way legacy LIMS showed them ("October 28, 2026 - 06:27 PM"). */
	_fmtDateTime(pDate)
	{
		if (!pDate) { return ''; }
		try
		{
			const tmpDay = this.pict.fable.Dates.dayJS(pDate);
			return tmpDay.isValid() ? tmpDay.format('MMMM DD, YYYY - hh:mm a') : this._fmtDate(pDate);
		}
		catch (pError) { return this._fmtDate(pDate); }
	}

	// --- entity names + tooltips (legacy LIMS {~Entity:~} / {~QuickSummary:~}) -------------------------------
	/**
	 * The entity popover button legacy LIMS put next to related records ({~QuickSummary:<Entity>^<ID>~}).
	 * @param {string} pEntity
	 * @param {any} pID
	 * @return {string} markup, or '' when the popover service isn't in this app or there is no ID
	 */
	_quickSummaryHTML(pEntity, pID)
	{
		const tmpID = Number(pID) || 0;
		if (!tmpID || !this.pict.services || !this.pict.services.HeadlightQuickSummaries) { return ''; }
		try { return this.pict.parseTemplate(`{~QuickSummary:${pEntity}^${tmpID}~}`, {}); }
		catch (pError) { return ''; }
	}

	/**
	 * Load the summary layouts the popovers use, once: legacy LIMS's defaults, overridden by the customer's
	 * LIMS-<Entity>-Summary rows. Best-effort; without them the popover builds one from the entity's schema.
	 * @return {Promise<void>}
	 */
	_ensureSummaryManifestsAsync()
	{
		if (!this._summaryManifestsLoad)
		{
			const tmpService = this.pict.services.ManifestPackagingService || this.pict.ManifestPackagingService;
			this._summaryManifestsLoad = (tmpService && (typeof tmpService.loadQuickSummaryManifests === 'function') && this.pict.services.HeadlightQuickSummaries)
				? tmpService.loadQuickSummaryManifests(Object.keys(DEFAULT_SUMMARY_MANIFESTS), JSON.parse(JSON.stringify(DEFAULT_SUMMARY_MANIFESTS)), LIMS_PRODUCT)
					.catch((pError) => { this.pict.log.warn(`SampleWorkspace: summary layouts did not load: ${(pError && pError.message) || pError}`); })
				: Promise.resolve();
		}
		return this._summaryManifestsLoad;
	}

	/**
	 * Fetch display names for records not already cached.
	 * @param {Record<number, string>} pCache - ID -> name, filled in place
	 * @param {string} pCollection - e.g. 'Users'
	 * @param {string} pIDField - e.g. 'IDUser'
	 * @param {Array<any>} pIDs
	 * @param {(pRecord: Record<string, any>) => string} fName
	 * @return {Promise<void>}
	 */
	async _loadNamesAsync(pCache, pCollection, pIDField, pIDs, fName)
	{
		const tmpMissing = [ ...new Set((pIDs || []).map((pID) => Number(pID) || 0)) ].filter((pID) => pID && !(pID in pCache));
		if (!tmpMissing.length) { return; }
		const tmpRows = await this._getJSON(`${pCollection}/FilteredTo/FBL~${pIDField}~INN~${tmpMissing.join(',')}/0/${tmpMissing.length}`);
		for (const tmpID of tmpMissing) { pCache[tmpID] = ''; }
		for (const tmpRow of (Array.isArray(tmpRows) ? tmpRows : []))
		{
			pCache[Number(tmpRow[pIDField])] = fName(tmpRow) || '';
		}
	}
	_loadUserNamesAsync(pIDs)
	{
		return this._loadNamesAsync(this._userNames, 'Users', 'IDUser', pIDs,
			(pUser) => `${pUser.NameFirst || ''} ${pUser.NameLast || ''}`.trim() || pUser.LoginID || pUser.Email || '');
	}
	_userName(pIDUser)
	{
		const tmpID = Number(pIDUser) || 0;
		if (!tmpID) { return ''; }
		return this._userNames[tmpID] || `User #${tmpID}`;
	}
	/** "on <date> by <user>", as legacy LIMS wrote it under each line item, test and log. */
	_createdLineHTML(pRecord)
	{
		const tmpWhen = this._fmtDateTime(pRecord && pRecord.CreateDate);
		const tmpWho = this._userName(pRecord && pRecord.CreatingIDUser);
		if (!tmpWhen && !tmpWho) { return ''; }
		return `<span class="sw-muted">${tmpWhen ? `on ${this._esc(tmpWhen)}` : ''}${tmpWho ? ` by ${this._esc(tmpWho)}` : ''}</span>`;
	}

	/**
	 * Names the tab rows show that aren't on the rows themselves (creating users, line item projects). Re-paints the
	 * tab once they arrive if it is still showing.
	 * @param {string} pTab
	 * @param {Array<any>} pUserIDs
	 * @param {Array<any>} [pProjectIDs]
	 */
	_resolveRowNames(pTab, pUserIDs, pProjectIDs)
	{
		this._projectNames = this._projectNames || {};
		const tmpBefore = JSON.stringify([ this._userNames, this._projectNames ]);
		Promise.all(
			[
				this._loadUserNamesAsync(pUserIDs),
				this._loadNamesAsync(this._projectNames, 'Projects', 'IDProject', pProjectIDs || [], (pProject) => pProject.Name || ''),
			])
			.then(() =>
			{
				if ((this._activeTab === pTab) && !this.isEditing && (JSON.stringify([ this._userNames, this._projectNames ]) !== tmpBefore))
				{
					this.repaintBody();
				}
			})
			.catch(() => { /* names stay as IDs */ });
	}

	renderLineItems()
	{
		if (this._renderTabPending('lineitems', 'line items'))
		{
			return;
		}
		const tmpRows = this.state.LineItems || [];
		const tmpCanEdit = this.canChangeSample();
		this._projectNames = this._projectNames || {};
		this._resolveRowNames('lineitems', tmpRows.map((pR) => pR.CreatingIDUser), tmpRows.map((pR) => pR.IDProject));
		const tmpBody = tmpRows.map((pR) =>
		{
			const tmpActions = tmpCanEdit ? `
				<td class="sw-row-actions">
					<button class="button is-tiny is-light" onclick="_Pict.providers.SampleWorkspace.promptEditLineItemQty(${Number(pR.IDSampleLineItemJoin) || 0}, ${Number(pR.IDLineItem) || 0}, ${Number(pR.RepresentedQuantity) || 0})" title="Edit quantity"><i class="fas fa-pen"></i></button>
					<button class="button is-tiny is-danger is-light" onclick="_Pict.providers.SampleWorkspace.removeLineItem(${Number(pR.IDSampleLineItemJoin) || 0})" title="Remove"><i class="fas fa-times"></i></button>
				</td>` : '<td></td>';
			const tmpProject = pR.IDProject ? (this._projectNames[Number(pR.IDProject)] || `Project #${pR.IDProject}`) : '';
			return `<tr>
				<td><span class="sw-cell-strong">${this._esc(pR.Name || pR.ItemNumber || '—')}</span> ${this._quickSummaryHTML('LineItem', pR.IDLineItem)}<div class="sw-subrow">${this._createdLineHTML(pR)}</div></td>
				<td>${this._esc(pR.Description || '')}</td>
				<td>${this._esc(tmpProject)} ${this._quickSummaryHTML('Project', pR.IDProject)}</td>
				<td class="sw-num">${this._esc(pR.RepresentedQuantity !== undefined ? pR.RepresentedQuantity : '')}</td>
				<td>${this._esc(pR.ItemCode || '')}</td>
				${tmpActions}
			</tr>`;
		}).join('');
		const tmpTable = tmpRows.length
			? `<div class="sw-tablewrap"><table class="sw-table"><thead><tr><th>Item</th><th>Description</th><th>Project</th><th class="sw-num">Represented Qty</th><th>Item Code</th><th></th></tr></thead><tbody>${tmpBody}</tbody></table></div>`
			: this._empty('No line items are linked to this sample.');
		const tmpSample = this.state.Sample || {};
		const tmpTotal = `<span class="sw-total">Sample represented quantity: <b>${this._esc((tmpSample.RepresentedQuantity !== undefined && tmpSample.RepresentedQuantity !== null && tmpSample.RepresentedQuantity !== '') ? tmpSample.RepresentedQuantity : '—')}</b></span>`;
		const tmpAddBtn = tmpCanEdit ? `<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.toggleLineItemAdd()"><span class="icon"><i class="fas fa-plus"></i></span><span>Add Line Item</span></button>` : '';
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', `${this._tabActionsHTML(`${tmpAddBtn}${tmpTotal}`)}${this._lineItemAddFormHTML()}${tmpTable}`);
		if (this.lineItemAddState.Open)
		{
			this._mountLineItemPickers();
		}
	}

	_lineItemAddFormHTML()
	{
		const tmpAdd = this.lineItemAddState;
		if (!tmpAdd.Open || !this.canChangeSample()) { return ''; }
		return `<div class="sw-add-form">
			<div class="sw-add-row">
				<div class="sw-add-field"><span class="sw-add-label">Line item's project</span><div id="SampleWorkspace-LI-ProjectPicker" class="sw-add-picker"></div></div>
				<div class="sw-add-field"><span class="sw-add-label">Line item</span><div id="SampleWorkspace-LI-LineItemPicker" class="sw-add-picker"></div></div>
				<div class="sw-add-field"><span class="sw-add-label">Represented quantity</span><input class="sw-input sw-add-qty" type="number" placeholder="0" value="${this._esc(tmpAdd.Quantity || '')}" oninput="_Pict.providers.SampleWorkspace.lineItemAddState.Quantity = this.value"></div>
			</div>
			<div class="sw-add-row">
				<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.addLineItem()">Add</button>
				<button class="button is-small is-light" onclick="_Pict.providers.SampleWorkspace.toggleLineItemAdd()">Cancel</button>
			</div>
		</div>`;
	}

	/** Mount the add form's entity pickers (pict-section-picker, server-side search), as the other workspaces do. */
	_mountLineItemPickers()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#SampleWorkspace-LI-ProjectPicker').length > 0)
		{
			const tmpPicker = tmpPickerProvider.createPicker('SampleWorkspace-LI-ProjectPicker',
				{
					DestinationAddress: '#SampleWorkspace-LI-ProjectPicker',
					ValueAddress: 'AppData.SampleWorkspace.LineItemAdd.PickerProject',
					Searchable: true,
					AllowClear: true,
					ClearLabel: '(none)',
					Placeholder: 'Search projects…',
					DataProvider: this._pickerDataProvider('Projects', 'IDProject', [ 'Name' ], () => '', (pRow) => pRow.Name),
					ResolveValue: this._pickerResolveValue('Project', 'IDProject', (pRow) => pRow.Name),
					OnChange: (pValue) => this.selectLineItemProject(pValue),
				});
			if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
			if (tmpPicker && (typeof tmpPicker.setValue === 'function')) { tmpPicker.setValue(this.lineItemAddState.IDProject || ''); }
		}
		this._mountLineItemPicker();
	}

	/** (Re)mount the Line Item picker for the project chosen in the add form. */
	_mountLineItemPicker()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#SampleWorkspace-LI-LineItemPicker').length < 1) { return; }
		const tmpAdd = this.lineItemAddState;
		const tmpPicker = tmpPickerProvider.createPicker('SampleWorkspace-LI-LineItemPicker',
			{
				DestinationAddress: '#SampleWorkspace-LI-LineItemPicker',
				ValueAddress: 'AppData.SampleWorkspace.LineItemAdd.PickerLineItem',
				Searchable: true,
				AllowClear: true,
				ClearLabel: '(none)',
				Placeholder: tmpAdd.IDProject ? 'Search line items…' : 'Pick a project first',
				DataProvider: this._pickerDataProvider('LineItems', 'IDLineItem', [ 'Name', 'ItemNumber', 'Description' ],
					() => (Number(this.lineItemAddState.IDProject) ? `FBV~IDProject~EQ~${Number(this.lineItemAddState.IDProject)}` : null),
					(pRow) => `${pRow.ItemNumber ? `${pRow.ItemNumber} — ` : ''}${pRow.Name || pRow.Description || ''}`),
				ResolveValue: this._pickerResolveValue('LineItem', 'IDLineItem', (pRow) => pRow.Name),
				OnChange: (pValue) => { this.lineItemAddState.IDLineItem = parseInt(pValue, 10) || 0; },
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
		if (tmpPicker && (typeof tmpPicker.setValue === 'function')) { tmpPicker.setValue(tmpAdd.IDLineItem || ''); }
	}

	/**
	 * A server-side search DataProvider for a picker: `(searchTerm, page) => Promise<{ results, hasMore }>`.
	 * @param {string} pCollection
	 * @param {string} pIDField
	 * @param {Array<string>} pSearchFields
	 * @param {() => ?string} fScope - extra FoxHound filter; null means list nothing yet
	 * @param {(pRow: Record<string, any>) => string} fText
	 */
	_pickerDataProvider(pCollection, pIDField, pSearchFields, fScope, fText)
	{
		const tmpPageSize = 25;
		return (pSearchTerm, pPage) =>
		{
			const tmpScope = fScope();
			if (tmpScope === null) { return Promise.resolve({ results: [], hasMore: false }); }
			const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
			let tmpFilter = `FBV~Deleted~EQ~0${tmpScope ? `~${tmpScope}` : ''}`;
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).replace(/~/g, '').trim();
			if (tmpTerm && tmpPickerProvider && (typeof tmpPickerProvider.buildSearchFilter === 'function'))
			{
				tmpFilter += `~${tmpPickerProvider.buildSearchFilter(pSearchFields, tmpTerm)}`;
			}
			const tmpStart = (Number(pPage) || 0) * tmpPageSize;
			return this._getJSON(`${pCollection}/FilteredTo/${tmpFilter}~FSF~${pSearchFields[0]}~ASC~0/${tmpStart}/${tmpPageSize}`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					return { results: tmpList.map((pRow) => ({ Value: Number(pRow[pIDField]), Text: fText(pRow) || `#${pRow[pIDField]}`, Record: pRow })), hasMore: (tmpList.length >= tmpPageSize) };
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	/** Resolve a preselected ID to { Value, Text } so the picker shows its name on first render. */
	_pickerResolveValue(pEntity, pIDField, fText)
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue) || 0;
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`${pEntity}/${tmpID}`)
				.then((pRow) => ({ Value: tmpID, Text: (pRow && pRow[pIDField] && fText(pRow)) || `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/**
	 * Ask for a line item's new represented quantity in the app's modal, then save it.
	 * @param {number} pIDJoin
	 * @param {number} pIDLineItem
	 * @param {number} pCurrentQty
	 * @return {Promise<void>}
	 */
	async promptEditLineItemQty(pIDJoin, pIDLineItem, pCurrentQty)
	{
		if (!this._guardChangeSample())
		{
			return;
		}
		const tmpModal = this.pict.views && this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || (typeof tmpModal.show !== 'function'))
		{
			this._toast('The quantity editor is not available.', 'error');
			return;
		}
		let tmpInput = null;
		const tmpChoice = await tmpModal.show(
			{
				title: 'Edit represented quantity',
				closeable: true,
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'save', Label: 'Save', Style: 'primary' } ],
				onOpen: (pDialog) =>
				{
					tmpInput = this._appendQuantityInput(pDialog, pCurrentQty);
				},
			});
		if ((tmpChoice !== 'save') || !tmpInput)
		{
			return;
		}
		await this.editLineItemQuantity(pIDJoin, pIDLineItem, tmpInput.value);
	}

	/**
	 * Put a labelled number input in a PSM dialog's body; Enter presses the dialog's Save button.
	 * @param {HTMLElement} pDialog
	 * @param {number} pCurrentQty
	 * @return {HTMLInputElement|null}
	 */
	_appendQuantityInput(pDialog, pCurrentQty)
	{
		const tmpBody = pDialog && pDialog.querySelector('.pict-modal-dialog-body');
		if (!tmpBody)
		{
			return null;
		}
		const tmpLabel = document.createElement('label');
		tmpLabel.className = 'label';
		tmpLabel.setAttribute('for', 'SampleWorkspace-LI-QtyInput');
		tmpLabel.textContent = 'Represented quantity';
		const tmpInput = document.createElement('input');
		tmpInput.id = 'SampleWorkspace-LI-QtyInput';
		tmpInput.className = 'input';
		tmpInput.type = 'number';
		tmpInput.step = 'any';
		tmpInput.value = String(Number(pCurrentQty) || 0);
		tmpInput.addEventListener('keydown', (pEvent) =>
		{
			if (pEvent.key !== 'Enter')
			{
				return;
			}
			const tmpSave = pDialog.querySelector('.pict-modal-btn[data-hash="save"]');
			if (tmpSave)
			{
				tmpSave.click();
			}
		});
		tmpBody.append(tmpLabel, tmpInput);
		tmpInput.focus();
		tmpInput.select();
		return tmpInput;
	}

	renderTestInstances()
	{
		if (this._renderTabPending('testinstances', 'tests'))
		{
			return;
		}
		const tmpRows = libSampleTestRules.buildTestSummary(this.state.TestInstanceBundle);
		const tmpCanChange = this.canChangeSample();
		const tmpAddBtn = tmpCanChange ? this._tabActionsHTML(`<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.openTestWizard()"><span class="icon"><i class="fas fa-flask-vial"></i></span><span>Add Tests</span></button>`) : '';
		if (!tmpRows.length)
		{
			this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', `${tmpAddBtn}${this._empty('No tests are linked to this sample.')}`);
			return;
		}
		this._resolveRowNames('testinstances', tmpRows.map((pRow) => pRow.TestInstance.CreatingIDUser));
		const tmpMarkdown = this.pict.providers.MarkdownProvider;
		const tmpBody = tmpRows.map((pRow) =>
		{
			const tmpTI = pRow.TestInstance;
			let tmpRemarks = '';
			if (pRow.Notes)
			{
				tmpRemarks = (tmpMarkdown && (typeof tmpMarkdown.getRemarksAsHTML === 'function')) ?
					tmpMarkdown.getRemarksAsHTML(pRow.Notes, { Clamp: true }) :
					this._esc(pRow.Notes);
				tmpRemarks += this._tiPendingHTML(pRow.NotesDocument);
			}
			const tmpDocuments = pRow.Documents.map((pEntry) =>
			{
				const tmpDoc = pEntry.Document;
				const tmpDocStatus = pEntry.Status ? ` <span class="sw-chip ${this._toneClass(pEntry.StatusTone)}" title="Form status">${this._esc(pEntry.Status)}</span>` : '';
				const tmpDocName = this._esc(tmpDoc.Name || `Document #${tmpDoc.IDDocument}`);
				return `<div class="sw-ti-doc"><a href="javascript:void(0)" onclick="_Pict.providers.SampleWorkspace.openDocument(${Number(tmpDoc.IDDocument) || 0})">${tmpDocName}</a> ${this._quickSummaryHTML('Document', tmpDoc.IDDocument)}${tmpDocStatus}</div>`;
			}).join('');
			return `<tr>
				<td><span class="sw-cell-strong">${this._esc(pRow.Name)}</span> ${this._quickSummaryHTML('TestInstance', tmpTI.IDTestInstance)}<div class="sw-subrow">${this._createdLineHTML(tmpTI)}</div></td>
				<td><span class="sw-chip ${this._tiStatusClass(pRow.Result)}">${this._esc(pRow.Result || '—')}</span>${this._tiPendingHTML(pRow.ResultDocument)}</td>
				<td>${this._esc(tmpTI.SampleTestNumber || '')}</td>
				<td class="sw-ti-remarks">${tmpRemarks || '<span class="sw-muted">N/A</span>'}</td>
				<td>${tmpDocuments || '<span class="sw-muted">None</span>'}</td>
				${tmpCanChange ? `<td class="sw-row-actions"><button class="button is-tiny is-danger is-light" onclick="_Pict.providers.SampleWorkspace.deleteTestInstance(${Number(tmpTI.IDTestInstance) || 0})" title="Delete this test"><i class="fas fa-trash"></i></button></td>` : '<td></td>'}
			</tr>`;
		}).join('');
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body',
			`${tmpAddBtn}<div class="sw-tablewrap"><table class="sw-table"><thead><tr><th>Test</th><th>Status</th><th>Test #</th><th>Remarks</th><th>Attached Documents</th><th></th></tr></thead><tbody>${tmpBody}</tbody></table></div>`);
	}

	/**
	 * Documents tab: the sample's documents in the DocumentFrameSwitcher — a rail to switch between them and each one open in
	 * its own frame running the longhand form page, so edits, saves and approval actions behave exactly as they do there.
	 * The switcher's frames outlive this markup (tab switches, repaints, Edit and back), so open work is kept while the
	 * sample is.
	 */
	renderDocuments()
	{
		if (this._renderTabPending('documents', 'documents'))
		{
			return;
		}
		const tmpHidden = Number(this.state.InaccessibleDocumentCount) || 0;
		const tmpRailNote = (tmpHidden > 0)
			? `There ${(tmpHidden === 1) ? 'is 1 document' : `are ${tmpHidden} documents`} for this sample you do not have access to. If you think this is incorrect, please reach out to a supervisor.`
			: '';
		const tmpAddBtn = this.canUpdate() ? this._tabActionsHTML(`
			<button class="button is-small is-light" onclick="_Pict.providers.SampleWorkspace.attachDocument(false)"><span class="icon"><i class="fas fa-paperclip"></i></span><span>Attach Document</span></button>
			<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.attachDocument(true)"><span class="icon"><i class="fas fa-plus"></i></span><span>New Document</span></button>`) : '';
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', `${tmpAddBtn}<div id="SampleWorkspace-HiddenMedia"></div><div id="SampleWorkspace-DocFrames" class="sw-docframes"></div>`);
		const tmpSwitcher = this.pict.providers.DocumentFrameSwitcher;
		if (!tmpSwitcher)
		{
			return;
		}
		const tmpGUID = this._guidSample;
		tmpSwitcher.attach(
			{
				OwnerKey: `Sample:${tmpGUID}`,
				OwnerMatch: (pHash) => this.isOwnHash(pHash),
				AnchorID: 'SampleWorkspace-DocFrames',
				Documents: this._switcherDocuments(),
				RailNote: tmpRailNote,
			});
		this._loadDocumentTestsAsync();
		this._loadDocumentMediaAsync();
		this._loadHiddenDocumentMediaAsync();
		const tmpPendingOpen = this._pendingDocumentOpen;
		this._pendingDocumentOpen = 0;
		if (tmpPendingOpen)
		{
			tmpSwitcher.open(tmpPendingOpen);
		}
	}

	/**
	 * The sample's documents for the switcher rail, in story order (creation form, tests, authorization), each with its
	 * form's name, the tests its form covers (and which of them this sample assigned to it) and how many media
	 * observations it holds.
	 * @return {Array<Object>}
	 */
	_switcherDocuments()
	{
		const tmpCatalog = this._formTestCatalogs[Number(this.state.Sample && this.state.Sample.IDCustomer) || 0];
		const tmpBundle = this.state.TestInstanceBundle;
		return libSampleTestRules.orderDocumentStory(this.state.Documents, tmpCatalog).map((pDocument) =>
		{
			const tmpType = tmpCatalog && tmpCatalog.Types[pDocument.DocumentType];
			return Object.assign({}, pDocument,
				{
					FormName: (tmpType && tmpType.FormName) || '',
					Tests: libSampleTestRules.getDocumentTests(pDocument, tmpCatalog, tmpBundle),
					Media: this._documentMedia.Counts[pDocument.IDDocument] || {},
				});
		});
	}

	/**
	 * Load what the rail needs to name each document's tests, then repaint it: the tests each form type declares (its
	 * RNI's Options.TestHashes, cached per customer) and this sample's test instances (the Tests tab's data). Two batched
	 * reads for the catalog, never one per document; the rail shows the plain labels until they land.
	 * @return {Promise<void>}
	 */
	async _loadDocumentTestsAsync()
	{
		const tmpIDSample = this._idSample;
		const tmpIDCustomer = Number(this.state.Sample && this.state.Sample.IDCustomer) || 0;
		if (!tmpIDSample || !tmpIDCustomer)
		{
			return;
		}
		const tmpCatalog = this._formTestCatalogs[tmpIDCustomer] || (this._formTestCatalogs[tmpIDCustomer] = { Types: {}, Tests: {} });
		const tmpNewTypes = [ ...new Set((this.state.Documents || []).map((pDocument) => pDocument.DocumentType)) ]
			.filter((pType) => pType && !tmpCatalog.Types[pType] && (pType.indexOf(',') < 0));
		await Promise.all([ this._ensureTabDataLoaded('testinstances'), this._loadFormTestTypesAsync(tmpCatalog, tmpIDCustomer, tmpNewTypes) ]);
		this._repaintSwitcherDocuments(tmpIDSample);
	}

	/**
	 * Count the media observations joined to each of the sample's documents, then repaint the rail. Paged joins, then
	 * the observations in batches; skipped when the document list has not changed since the last count.
	 * @return {Promise<void>}
	 */
	async _loadDocumentMediaAsync()
	{
		const tmpIDSample = this._idSample;
		const tmpIDDocuments = (this.state.Documents || []).map((pDocument) => Number(pDocument.IDDocument)).filter((pID) => pID > 0);
		const tmpKey = `${tmpIDSample}:${tmpIDDocuments.join(',')}`;
		if (!tmpIDSample || !tmpIDDocuments.length || (this._documentMedia.Key === tmpKey))
		{
			return;
		}
		const tmpRead = await this._readDocumentObservationsAsync(tmpIDDocuments);
		if (!tmpRead || (this._idSample !== tmpIDSample))
		{
			return;
		}
		this._documentMedia = { Key: tmpKey, Counts: libSampleDocumentDetails.countDocumentMedia(tmpRead.Joins, tmpRead.Observations) };
		this._repaintSwitcherDocuments(tmpIDSample);
	}

	/**
	 * Load the photos and videos of the sample's documents the user may not open, then paint them above the switcher.
	 * The joins come from the sample's media route; the observations are read through their own gate, so only media
	 * the user may already read comes back.
	 * @return {Promise<void>}
	 */
	async _loadHiddenDocumentMediaAsync()
	{
		const tmpIDSample = this._idSample;
		const tmpIDDocuments = (this.state.InaccessibleDocumentIDs || []).filter((pID) => pID > 0);
		const tmpKey = `${tmpIDSample}:${tmpIDDocuments.join(',')}`;
		if (this._hiddenDocumentMedia.Key === tmpKey)
		{
			this._renderHiddenDocumentMedia(tmpIDSample);
			return;
		}
		let tmpRead = { Joins: [], Observations: [] };
		if (tmpIDDocuments.length && Array.isArray(this.state.HiddenDocumentObservationJoins))
		{
			const tmpObservations = await this._readObservationsAsync(this.state.HiddenDocumentObservationJoins);
			tmpRead = tmpObservations ? { Joins: this.state.HiddenDocumentObservationJoins, Observations: tmpObservations } : null;
		}
		else if (tmpIDDocuments.length)
		{
			tmpRead = await this._readDocumentObservationsAsync(tmpIDDocuments);
		}
		if (!tmpRead || (this._idSample !== tmpIDSample))
		{
			return;
		}
		this._hiddenDocumentMedia = { Key: tmpKey, Media: libSampleDocumentDetails.listDocumentMedia(tmpRead.Joins, tmpRead.Observations) };
		this._renderHiddenDocumentMedia(tmpIDSample);
	}

	/**
	 * The observations joined to some documents: paged joins, then the observations in batches.
	 * @param {Array<number>} pIDDocuments
	 * @return {Promise<{ Joins: Array<Object>, Observations: Array<Object> }|null>} null when a read fails
	 */
	async _readDocumentObservationsAsync(pIDDocuments)
	{
		const tmpJoins = [];
		for (let tmpOffset = 0; ; tmpOffset += 250)
		{
			const tmpPage = await this._getJSON(`DocumentObservationJoins/FilteredTo/FBL~IDDocument~INN~${pIDDocuments.join(',')}~FBV~Deleted~EQ~0/${tmpOffset}/250`);
			if (!Array.isArray(tmpPage))
			{
				return null;
			}
			tmpJoins.push(...tmpPage);
			if (tmpPage.length < 250)
			{
				break;
			}
		}
		const tmpObservations = await this._readObservationsAsync(tmpJoins);
		return tmpObservations ? { Joins: tmpJoins, Observations: tmpObservations } : null;
	}

	/**
	 * The live observations named by some document-observation joins, in batches, as the reader may read them.
	 * @param {Array<{ IDObservation: number }>} pJoins
	 * @return {Promise<Array<Object>|null>} null when a read fails
	 */
	async _readObservationsAsync(pJoins)
	{
		const tmpIDObservations = [ ...new Set(pJoins.map((pJoin) => Number(pJoin.IDObservation)).filter((pID) => pID > 0)) ];
		const tmpObservations = [];
		for (let tmpStart = 0; tmpStart < tmpIDObservations.length; tmpStart += 200)
		{
			const tmpBatch = tmpIDObservations.slice(tmpStart, tmpStart + 200);
			const tmpPage = await this._getJSON(`Observations/FilteredTo/FBL~IDObservation~INN~${tmpBatch.join(',')}~FBV~Deleted~EQ~0/0/${tmpBatch.length}`);
			if (!Array.isArray(tmpPage))
			{
				return null;
			}
			tmpObservations.push(...tmpPage);
		}
		return tmpObservations;
	}

	/**
	 * Paint the hidden documents' photos and videos as a strip of thumbnails, each opening the full image in a new tab.
	 * Empty, and so hidden, when there are none.
	 * @param {number} pIDSample - the sample the media was loaded for
	 */
	_renderHiddenDocumentMedia(pIDSample)
	{
		const tmpHost = (typeof document !== 'undefined') ? document.querySelector('#SampleWorkspace-HiddenMedia') : null;
		if (!tmpHost || (this._idSample !== pIDSample) || (this._activeTab !== 'documents'))
		{
			return;
		}
		tmpHost.innerHTML = '';
		const tmpMedia = this._hiddenDocumentMedia.Media || [];
		if (!tmpMedia.length)
		{
			return;
		}
		const tmpRender = this.pict.HeadlightObservationRender;
		const fImageURL = (pIDObservation, pSize) => ((tmpRender && (typeof tmpRender.observationImageURL === 'function')) ? tmpRender.observationImageURL(pIDObservation, pSize) : `/1.0/Observation/${pIDObservation}/Image/${pSize}`);

		const tmpPanel = document.createElement('div');
		tmpPanel.className = 'sw-hidden-media';
		const tmpTitle = document.createElement('div');
		tmpTitle.className = 'sw-hidden-media-title';
		const tmpIcon = document.createElement('i');
		tmpIcon.className = 'fas fa-images';
		const tmpCount = document.createElement('span');
		tmpCount.className = 'sw-muted';
		tmpCount.textContent = String(tmpMedia.length);
		tmpTitle.append(tmpIcon, ' Photos and videos from documents you cannot open ', tmpCount);
		const tmpStrip = document.createElement('div');
		tmpStrip.className = 'sw-hidden-media-strip';
		for (const tmpItem of tmpMedia)
		{
			const tmpLink = document.createElement('a');
			tmpLink.className = 'sw-hidden-media-item';
			tmpLink.href = fImageURL(tmpItem.IDObservation, 'Standard');
			tmpLink.target = '_blank';
			tmpLink.rel = 'noopener';
			tmpLink.title = tmpItem.Name;
			const tmpImage = document.createElement('img');
			tmpImage.src = fImageURL(tmpItem.IDObservation, 'Thumbnail');
			tmpImage.alt = tmpItem.Name;
			tmpImage.loading = 'lazy';
			tmpLink.append(tmpImage);
			if (tmpItem.Kind === 'video')
			{
				const tmpPlay = document.createElement('i');
				tmpPlay.className = 'fas fa-circle-play sw-hidden-media-play';
				tmpLink.append(tmpPlay);
			}
			tmpStrip.append(tmpLink);
		}
		tmpPanel.append(tmpTitle, tmpStrip);
		tmpHost.append(tmpPanel);
	}

	/**
	 * Give the switcher the enriched document list, if it is still showing this sample's documents.
	 * @param {number} pIDSample - the sample the data was loaded for
	 */
	_repaintSwitcherDocuments(pIDSample)
	{
		const tmpSwitcher = this.pict.providers.DocumentFrameSwitcher;
		if ((this._idSample !== pIDSample) || (this._activeTab !== 'documents') || !tmpSwitcher || (tmpSwitcher.OwnerKey !== `Sample:${this._guidSample}`))
		{
			return;
		}
		tmpSwitcher.setDocuments(this._switcherDocuments());
	}

	/**
	 * Add form types to a customer's catalog: their RNIs, then the tests those declare.
	 * @param {import('@paviasystems/headlight-samples/source/SampleTest-Rules.js').FormTestCatalog} pCatalog
	 * @param {number} pIDCustomer
	 * @param {Array<String>} pTypes - DocumentTypes not yet in the catalog
	 * @return {Promise<void>}
	 */
	async _loadFormTestTypesAsync(pCatalog, pIDCustomer, pTypes)
	{
		if (!pTypes.length)
		{
			return;
		}
		const tmpRNIs = await this._getJSON(`ReportNamedInstances/FilteredTo/FBL~Hash~INN~${pTypes.map(encodeURIComponent).join(',')}~FBV~IDCustomer~EQ~${pIDCustomer}~FBV~Deleted~EQ~0/0/250`);
		if (!Array.isArray(tmpRNIs))
		{
			return;
		}
		const tmpHashesByType = {};
		for (const tmpRNI of tmpRNIs)
		{
			tmpHashesByType[tmpRNI.Hash] = { FormName: tmpRNI.Name || '', TestHashes: libSampleTestRules.getRNITestHashes(tmpRNI), StoryRole: libSampleTestRules.getRNIStoryRole(tmpRNI) };
		}
		const tmpTestHashes = [ ...new Set(Object.values(tmpHashesByType).flatMap((pType) => pType.TestHashes)) ]
			.filter((pHash) => !pCatalog.Tests[pHash] && (pHash.indexOf(',') < 0));
		if (tmpTestHashes.length)
		{
			const tmpTests = await this._getJSON(`Tests/FilteredTo/FBL~Hash~INN~${tmpTestHashes.map(encodeURIComponent).join(',')}~FBV~IDCustomer~EQ~${pIDCustomer}~FBV~Deleted~EQ~0/0/250`);
			if (!Array.isArray(tmpTests))
			{
				return;
			}
			for (const tmpTest of tmpTests)
			{
				pCatalog.Tests[tmpTest.Hash] = { Name: tmpTest.Name || '' };
			}
		}
		for (const tmpType of pTypes)
		{
			pCatalog.Types[tmpType] = tmpHashesByType[tmpType] || { FormName: '', TestHashes: [], StoryRole: '' };
		}
	}

	/**
	 * The documents joined to the sample that did not come back from the Documents read, i.e. the ones the user may not
	 * read. Distinct, so a document joined twice is listed once.
	 * @param {Array<Object>} pJoins - the sample's DocumentPolyJoin rows
	 * @param {Array<Object>} pDocuments - the documents the user can read
	 * @return {Array<number>}
	 */
	_inaccessibleDocumentIDs(pJoins, pDocuments)
	{
		const tmpReadable = new Set((pDocuments || []).map((pDocument) => Number(pDocument.IDDocument)));
		const tmpJoined = new Set((Array.isArray(pJoins) ? pJoins : []).map((pJoin) => Number(pJoin.IDDocument)).filter((pID) => pID > 0));
		return [ ...tmpJoined ].filter((pID) => !tmpReadable.has(pID));
	}

	/**
	 * Whether a location hash is still this sample: its tab routes (`#/Sample/Workspace/<ID>[/<tab>]`, by ID) or its
	 * view and edit routes (by GUID). The document switcher closes its frames on any other hash, so it must not read a
	 * tab switch as leaving the sample.
	 * @param {string} pHash
	 * @return {boolean}
	 */
	isOwnHash(pHash)
	{
		const tmpHash = String(pHash || '');
		if (this._guidSample && (tmpHash.indexOf(this._guidSample) > -1))
		{
			return true;
		}
		const tmpMatch = /^#?\/Sample\/Workspace\/(\d+)(?:[/?]|$)/.exec(tmpHash);
		return !!(tmpMatch && this._idSample && (Number(tmpMatch[1]) === Number(this._idSample)));
	}

	/**
	 * Open one of the sample's documents in the Documents tab's switcher.
	 * @param {number} pIDDocument
	 */
	openDocument(pIDDocument)
	{
		const tmpID = Number(pIDDocument) || 0;
		if (!tmpID)
		{
			return;
		}
		this._pendingDocumentOpen = tmpID;
		if (this._activeTab === 'documents')
		{
			this.repaintBody();
		}
		else
		{
			this.navigateTab('documents');
		}
	}

	/**
	 * Go to a sample's Documents tab with one of its documents open (from a list elsewhere, such as a dashboard).
	 * @param {number} pIDSample
	 * @param {number} pIDDocument
	 */
	openSampleDocument(pIDSample, pIDDocument)
	{
		const tmpIDSample = Number(pIDSample) || 0;
		const tmpIDDocument = Number(pIDDocument) || 0;
		if (!tmpIDSample || !tmpIDDocument)
		{
			return;
		}
		if (tmpIDSample === Number(this._idSample))
		{
			return this.openDocument(tmpIDDocument);
		}
		this._pendingDocumentOpen = tmpIDDocument;
		this.pict.providers.PictRouter.navigate(`/Sample/Workspace/${tmpIDSample}/documents`);
	}

	renderCores()
	{
		if (this._renderTabPending('cores', 'cores'))
		{
			return;
		}
		const tmpRows = this.state.Cores || [];
		if (!tmpRows.length) { this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', this._empty('No cores are linked to this sample.')); return; }
		const tmpBody = tmpRows.map((pC) => `<tr>
			<td class="sw-cell-strong">${this._esc(pC.SublotName || pC.QRCode || `Core #${pC.IDCore}`)}</td>
			<td>${this._esc(pC.QRCode || '')}</td>
			<td class="sw-muted">${this._esc(this._fmtDate(pC.CreateDate))}</td>
		</tr>`).join('');
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body',
			`<div class="sw-tablewrap"><table class="sw-table"><thead><tr><th>Core</th><th>QR Code</th><th>Created</th></tr></thead><tbody>${tmpBody}</tbody></table></div>`);
	}

	renderLogs()
	{
		const tmpRows = this.state.Logs || [];
		this._resolveRowNames('logs', tmpRows.map((pL) => pL.CreatingIDUser));
		const tmpUtility = this.pict.providers.UtilityProvider;
		const fMap = (pL) =>
		{
			// Where the log was captured, as legacy LIMS showed it; a log without a location gets a plain marker.
			const tmpLat = Number(pL.CaptureLatitude || pL.UserLatitude) || 0;
			const tmpLng = Number(pL.CaptureLongitude || pL.UserLongitude) || 0;
			let tmpURL = '';
			if (tmpLat && tmpLng && tmpUtility && (typeof tmpUtility.getGoogleMapURLFromEntityRecord === 'function'))
			{
				try { tmpURL = tmpUtility.getGoogleMapURLFromEntityRecord(pL, { size: '100x100' }); }
				catch (pError) { tmpURL = ''; }
			}
			return tmpURL
				? `<img class="sw-log-map" src="${this._esc(tmpURL)}" alt="Where this log was captured" loading="lazy">`
				: '<div class="sw-log-map sw-log-nomap" title="No location recorded"><i class="fas fa-location-dot"></i></div>';
		};
		const tmpAdd = `<div class="sw-log-add">
			<textarea class="sw-input sw-log-input" placeholder="Add a note…" oninput="_Pict.AppData.SampleWorkspace.NewLogNotes = this.value"></textarea>
			<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.addLog()"><span class="icon"><i class="fas fa-plus"></i></span><span>Add Log</span></button>
		</div>`;
		const tmpList = tmpRows.length
			? tmpRows.map((pL) => `<div class="sw-log">
				${fMap(pL)}
				<div class="sw-log-main">
					<div class="sw-log-id sw-muted">#${this._esc(pL.IDSampleLog || '')}</div>
					<div class="sw-log-notes">${this._esc(pL.Notes || '')}</div>
					<div class="sw-log-meta">${pL.FromStatus || pL.ToStatus ? `<span>${this._esc(this.resolveSampleStatusName(pL.FromStatus) || '')} → ${this._esc(this.resolveSampleStatusName(pL.ToStatus) || '')}</span>` : ''}${this._createdLineHTML(pL)}</div>
				</div>
			</div>`).join('')
			: this._empty('No log entries yet.');
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', `<div class="sw-logs">${tmpAdd}<div class="sw-log-list">${tmpList}</div></div>`);
	}

	/** Location tab: where the sample is, or the cart for moving it (see SampleWorkspace-LocationTab.js). */
	renderLocation() { this._locationTab.render(); }
	/** @return {Promise<void>} the Location tab's OnEnter: read where the sample is, once */
	ensureLocationLoaded() { return this._locationTab.ensureLoadedAsync(); }
	/** Location tab's Change location / Set location. */
	editLocation() { this._locationTab.edit(); }
	/** Location tab's Cancel: back to the display, nothing moved. */
	cancelLocationEdit() { this._locationTab.cancel(); }

	/**
	 * Print tab: the label drawn upright so it reads on screen (the printer gets it turned sideways), plus the print
	 * buttons. Same label, layout and customer manifest as Print.
	 */
	renderPrint()
	{
		const tmpSample = this.state.Sample || {};
		if (!tmpSample.QRCode)
		{
			this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', this._empty('This sample has no QR code to print.'));
			return;
		}
		this.pict.ContentAssignment.assignContent('#SampleWorkspace-Body', `
			<div class="sw-print">
				<div id="SampleWorkspace-PrintPreview" class="sw-print-preview"><span class="sw-muted">Drawing the label…</span></div>
				<div class="sw-toolbar">
					<button class="button is-small is-link" onclick="_Pict.providers.SampleWorkspace.printLabel()"><span class="icon"><i class="fas fa-print"></i></span><span>Print QR Code Label</span></button>
					<button class="button is-small is-light" onclick="_Pict.providers.SampleWorkspace.printLabel(true)">Print options…</button>
				</div>
			</div>`);
		this._renderPrintPreviewAsync();
	}

	async _renderPrintPreviewAsync()
	{
		const tmpSample = this.state.Sample || {};
		const tmpQR = this.pict.providers.QRCodeProvider;
		if (!tmpQR || !tmpSample.QRCode) { return; }
		try
		{
			await tmpQR.ensurePrintableManifests();
			if (this._activeTab !== 'print') { return; }
			await tmpQR.printDecoratedEntity('Sample', tmpSample.QRCode, 'SampleWorkspace-PrintPreview', undefined, tmpSample);
		}
		catch (pError)
		{
			this.pict.log.warn(`SampleWorkspace: label preview failed: ${(pError && pError.message) || pError}`);
		}
	}

	// --- Header helpers (mode-aware header parts read by the config) ------------------------------------------

	/** Header title: "New Sample" in create; otherwise the sample name + a status pill (markup passes through). */
	headerTitle()
	{
		const tmpSample = this.state.Sample || {};
		if (this._mode === 'create') { return 'New Sample'; }
		// Always markup: pict-section-workspace escapes a title again unless it contains a tag (_escTitle).
		const tmpName = `<span class="sw-title-name">${tmpSample.Name ? this._esc(tmpSample.Name) : '(unnamed sample)'}</span>`;
		const tmpStatus = tmpSample.Status ? `<span class="sw-status ${this._statusClass(tmpSample.Status)}">${this._esc(this.resolveSampleStatusName(tmpSample.Status))}</span>` : '';
		const tmpLock = this.isLocked() ? ` <i class="fas fa-lock sw-lock" title="${this.canOverrideLock() ? 'Locked. You can still change it.' : 'Locked'}"></i>` : '';
		return `${tmpName} ${tmpStatus}${tmpLock}`;
	}

	/** Header subline (view mode): PSID + QR code. */
	headerSubtitle()
	{
		const tmpSample = this.state.Sample || {};
		if (this._mode === 'create') { return ''; }
		const tmpBits = [];
		if (tmpSample.LabControlNumber) { tmpBits.push(`<span class="sw-psid">PSID ${this._esc(tmpSample.LabControlNumber)}</span>`); }
		if (tmpSample.QRCode) { tmpBits.push(`<span class="sw-qr"><i class="fas fa-qrcode"></i> ${this._esc(tmpSample.QRCode)}</span>`); }
		return tmpBits.length ? tmpBits.join('<span class="sw-dot">·</span>') : '';
	}

	/** Header extra (view mode): a QR thumbnail host mounted after render by headerAfterRender. */
	headerExtra()
	{
		const tmpSample = this.state.Sample || {};
		return (this._mode !== 'create' && tmpSample.QRCode) ? '<div class="sw-qr-thumb" id="SampleWorkspace-QR"></div>' : '';
	}
	headerAfterRender()
	{
		const tmpSample = this.state.Sample || {};
		if (this._mode !== 'create' && tmpSample.QRCode && (typeof this.renderQRThumbnail === 'function')) { this.renderQRThumbnail('#SampleWorkspace-QR'); }
	}
}

module.exports = SampleWorkspaceProvider;
module.exports.TABS = TABS;
